import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  FaChevronLeft,
  FaChevronRight,
  FaStickyNote,
  FaTimes,
} from "react-icons/fa";
import { getHighlightColorOptions } from "../../constants/highlightColors";
import useReaderStore from "../../context/readerStore";
import {
  useIsMobile,
  useIsTablet,
  useIsTouchTablet,
} from "../../hooks/useMediaQuery";
import useSelectionTranslation, {
  buildTranslationAnchor,
} from "../../hooks/useSelectionTranslation";
import { devDebug, devLog } from "../../logger";
import { getAiTargetLanguage } from "../../services/aiService";
import { getBookId } from "../../services/bookmarkService";
import {
  getCopyCharLimit,
  isCopyAllowed,
  isFeatureAllowed,
  shouldIsolateCoverInDoublePage,
  truncateForCopy,
} from "../../services/drmPolicyService";
import {
  applyEpubSearchHighlights,
  clearAllEpubSearchHighlights,
  isSearchHighlightAnnotation,
  resetSearchHighlightTracking,
} from "../../services/epubSearchHighlightService";
import {
  getCurrentSectionIndex,
  isLegacySavedEpubPage,
} from "../../services/epubService";
import {
  createHighlight,
  editHighlight,
  removeHighlightById,
} from "../../services/highlightService";
import { createNote, editNote } from "../../services/noteService";
import {
  showCopyLimitExceededNotification,
  showCopyNotAllowedNotification,
  showOperationFailedNotification,
  showSessionExpiredNotification,
} from "../../services/notificationService";
import {
  createUnderline,
  deleteUnderline,
} from "../../services/underlineService";
import ContextMenu from "../ContextMenu/ContextMenu";
import TranslationPopover from "../ContextMenu/TranslationPopover";
import Pullouts from "../Pullouts/Pullouts";
import "../../utils/patchMarksPane";
import {
  applyEpubReaderTheme,
  EPUB_LINE_HEIGHT_DEFAULT,
} from "../../utils/epubThemes";
import {
  applyEpubReadingDirection,
  READING_DIRECTION_RTL,
  syncEpubLayoutDirection,
} from "../../utils/readingDirection";
import "./EpubViewer.css";

// Highlight color theme for EPUB (single class per color; epub.js uses classList.add).
// Actual SVG fill comes from getEpubHighlightStyles attributes; these rules keep
// iframe/theme injection consistent. Do not set parent-CSS fills — that breaks blend.
const EPUB_HIGHLIGHT_THEME = {
  ".epub-highlight-red": {
    background: "rgba(242, 139, 130, 1.0)",
    "mix-blend-mode": "multiply",
    cursor: "pointer",
  },
  ".epub-highlight-orange": {
    background: "rgba(247, 184, 138, 1.0)",
    "mix-blend-mode": "multiply",
    cursor: "pointer",
  },
  ".epub-highlight-yellow": {
    background: "rgba(255, 245, 157, 1.0)",
    "mix-blend-mode": "multiply",
    cursor: "pointer",
  },
  ".epub-highlight-green": {
    background: "rgba(165, 214, 167, 1.0)",
    "mix-blend-mode": "multiply",
    cursor: "pointer",
  },
  ".epub-highlight-light-blue": {
    background: "rgba(144, 202, 249, 1.0)",
    "mix-blend-mode": "multiply",
    cursor: "pointer",
  },
  ".epub-highlight-purple": {
    background: "rgba(206, 147, 216, 1.0)",
    "mix-blend-mode": "multiply",
    cursor: "pointer",
  },
  ".epub-underline": { cursor: "pointer" },
  ".epub-search-match": {
    background: "rgba(255, 157, 59, 0.34)",
    "box-shadow": "0 0 0 1px rgba(255, 235, 59, 0.22) inset",
    cursor: "default",
  },
};
const EPUB_HIGHLIGHT_OPACITY = "1.0";
const EPUB_MAX_SCALE = 2.0;

/**
 * Force-render all spine items for continuous scrolled-doc mode.
 * Not used in paginated mode where epub.js handles pagination internally.
 *
 * Key epub.js internals (from source inspection):
 *  - manager.append(section) creates a view + iframe but does NOT load content
 *  - view.display(request)   actually loads the section HTML into the iframe
 *  - manager.update()        DESTROYS non-visible views on every scroll tick
 *  - manager.trim()          erases views outside the displayed range
 *
 * After pre-rendering we patch update/trim so views stay alive while scrolling.
 */
// eslint-disable-next-line no-unused-vars
const forceRenderAllSpineItems = async (rendition) => {
  try {
    const manager = rendition?.manager;
    const spine = rendition?.book?.spine;
    if (!manager || !spine) return;

    const total = spine.spineItems?.length ?? spine.length ?? 0;
    if (total <= 1) return;

    // --- prevent the manager from tearing down views ---
    // MUST be patched BEFORE the loop: as we append/display views the scroll
    // container grows, which fires onScroll → check() → the original update()
    // which would destroy the non-visible views we just loaded.
    manager.update = function () {
      return Promise.resolve();
    };
    manager.trim = function () {
      return Promise.resolve();
    };

    // --- append every section AFTER the last rendered view ---
    let lastView = manager.views?.last?.();
    let guard = total;
    while (lastView?.section && guard-- > 0) {
      const next =
        typeof lastView.section.next === "function"
          ? lastView.section.next()
          : null;
      if (!next) break;
      const view = manager.append(next);
      try {
        await view.display(manager.request);
        view.show();
      } catch (_) {
        break;
      }
      lastView = manager.views.last();
    }

    // --- prepend every section BEFORE the first rendered view ---
    // ContinuousViewManager.prepend() registers a RESIZED listener that
    // calls counter() → scrollBy() to compensate for content added above.
    let firstView = manager.views?.first?.();
    guard = total;
    while (firstView?.section && guard-- > 0) {
      const prev =
        typeof firstView.section.prev === "function"
          ? firstView.section.prev()
          : null;
      if (!prev) break;
      const view = manager.prepend(prev);
      try {
        await view.display(manager.request);
        view.show();
      } catch (_) {
        break;
      }
      firstView = manager.views.first();
    }
  } catch (err) {
    console.warn("Could not pre-render all EPUB spine items:", err);
  }
};

// Persist undo/redo stacks by document so they survive component remounts (e.g. HMR, navigation)
const epubUndoRedoCache = new Map();

const getEpubStacksKey = () =>
  useReaderStore.getState().documentId || "epub-default";

/** Subpixel slack for epub.js paginated scroll width checks (Windows + narrow columns). */
const EPUB_SCROLL_EPSILON_PX = 2;

const isCoarsePointerDevice = () =>
  typeof window !== "undefined" &&
  Boolean(window.matchMedia?.("(pointer: coarse)")?.matches);

/** Mobile / coarse pointer only — same gate as PdfViewerNew selection pan fix. */
const shouldGuardEpubSelectionPan = (isMobile) =>
  Boolean(isMobile) || isCoarsePointerDevice();

/**
 * While a text selection is active, restore scroll on epub.js column container
 * + iframe scrolling roots so mobile long-press / handle-drag does not pan the
 * zoomed (or column-paged) view. Mirrors PdfViewerNew scroll lock.
 */
const attachEpubSelectionScrollLock = ({
  doc,
  win,
  getManagerContainer,
}) => {
  const PAN_SLOP_PX = 12;
  let provisional = null;
  let locked = null;
  let touchOnText = false;
  let touchStartX = 0;
  let touchStartY = 0;
  let mgrListening = null;
  let cleanedUp = false;

  const isDocAlive = () => {
    try {
      return Boolean(doc?.defaultView && win && !win.closed);
    } catch {
      return false;
    }
  };

  const collectScrollables = () => {
    const list = [];
    const mgr = getManagerContainer?.();
    if (mgr) list.push(mgr);
    const se = doc.scrollingElement || doc.documentElement;
    if (se) list.push(se);
    if (doc.body && doc.body !== se) list.push(doc.body);
    return Array.from(new Set(list));
  };

  const snapshot = () =>
    collectScrollables().map((el) => ({
      el,
      left: el.scrollLeft,
      top: el.scrollTop,
    }));

  const restore = () => {
    if (!locked) return;
    for (const { el, left, top } of locked) {
      if (!el) continue;
      if (el.scrollLeft !== left) el.scrollLeft = left;
      if (el.scrollTop !== top) el.scrollTop = top;
    }
  };

  const hasSelection = () => {
    try {
      const sel = win.getSelection?.();
      return Boolean(sel && !sel.isCollapsed && sel.toString().trim());
    } catch {
      return false;
    }
  };

  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    provisional = null;
    locked = null;
    touchOnText = false;
    try {
      doc.removeEventListener("selectionchange", onSelectionChange);
      doc.removeEventListener("touchstart", onTouchStart, true);
      doc.removeEventListener("touchmove", onTouchMove, true);
      doc.removeEventListener("touchend", onTouchEnd, true);
      doc.removeEventListener("touchcancel", onTouchEnd, true);
      win.removeEventListener("scroll", onScroll, true);
      win.removeEventListener("pagehide", cleanup);
      win.removeEventListener("unload", cleanup);
    } catch {
      // iframe may already be torn down
    }
    if (mgrListening) {
      mgrListening.removeEventListener("scroll", onScroll);
      mgrListening = null;
    }
  };

  const onScroll = () => {
    // Chapter iframe gone — drop parent manager listener so it cannot
    // restore a stale scrollLeft from a previous spine item.
    if (!isDocAlive()) {
      cleanup();
      return;
    }
    if (!locked) return;
    if (!hasSelection()) {
      locked = null;
      return;
    }
    restore();
  };

  const ensureManagerScrollListener = () => {
    if (cleanedUp) return;
    const mgr = getManagerContainer?.();
    if (!mgr || mgr === mgrListening) return;
    if (mgrListening) {
      mgrListening.removeEventListener("scroll", onScroll);
    }
    mgr.addEventListener("scroll", onScroll, { passive: true });
    mgrListening = mgr;
  };

  const onSelectionChange = () => {
    if (cleanedUp || !isDocAlive()) {
      cleanup();
      return;
    }
    ensureManagerScrollListener();
    if (hasSelection()) {
      locked = provisional || locked || snapshot();
      provisional = null;
    } else if (!touchOnText) {
      locked = null;
    }
  };

  const onTouchStart = (evt) => {
    if (cleanedUp || !isDocAlive()) {
      cleanup();
      return;
    }
    if (evt.touches?.length > 1) {
      touchOnText = false;
      provisional = null;
      locked = null;
      return;
    }
    ensureManagerScrollListener();
    touchOnText = true;
    const touch = evt.touches?.[0];
    touchStartX = touch?.clientX || 0;
    touchStartY = touch?.clientY || 0;
    provisional = snapshot();
  };

  const onTouchMove = (evt) => {
    if (!touchOnText || locked || !provisional) return;
    if (hasSelection()) return;
    const touch = evt.touches?.[0];
    if (!touch) return;
    if (
      Math.abs(touch.clientX - touchStartX) > PAN_SLOP_PX ||
      Math.abs(touch.clientY - touchStartY) > PAN_SLOP_PX
    ) {
      provisional = null;
      touchOnText = false;
    }
  };

  const onTouchEnd = () => {
    touchOnText = false;
    provisional = null;
    if (!hasSelection()) locked = null;
  };

  try {
    if (!doc.head?.querySelector?.("[data-igp-selection-pan-fix]")) {
      const style = doc.createElement("style");
      style.setAttribute("data-igp-selection-pan-fix", "1");
      style.textContent = "html, body { overflow-anchor: none; }";
      doc.head?.appendChild(style);
    }
  } catch {
    // ignore
  }

  doc.addEventListener("selectionchange", onSelectionChange);
  doc.addEventListener("touchstart", onTouchStart, {
    passive: true,
    capture: true,
  });
  doc.addEventListener("touchmove", onTouchMove, {
    passive: true,
    capture: true,
  });
  doc.addEventListener("touchend", onTouchEnd, {
    passive: true,
    capture: true,
  });
  doc.addEventListener("touchcancel", onTouchEnd, {
    passive: true,
    capture: true,
  });
  win.addEventListener("scroll", onScroll, { passive: true, capture: true });
  win.addEventListener("pagehide", cleanup);
  win.addEventListener("unload", cleanup);
  ensureManagerScrollListener();
};

const getLocationCfi = (location) =>
  location?.start?.cfi || location?.end?.cfi || null;

const getDisplayedPage = (location) => location?.end?.displayed?.page;

const clampNumber = (value, min, max) =>
  Math.min(max, Math.max(min, Number(value) || min));

const getChapterPageBounds = (chapter) => {
  const startPage = Number(chapter?.start_page) || 1;
  const pageCount = Math.max(1, Number(chapter?.page_count) || 1);
  const endPage =
    Number(chapter?.end_page) || startPage + Math.max(0, pageCount - 1);

  return {
    startPage,
    endPage: Math.max(startPage, endPage),
    pageCount: Math.max(1, endPage - startPage + 1),
  };
};

const getEpubReadingDirection = (rendition) => {
  // Prefer Layout direction — that is what formats columns. rendition.settings
  // alone can be rtl while Layout is still ltr (epub.js direction() gap).
  const layoutDir = rendition?._layout?.settings?.direction;
  const settingsDir = rendition?.settings?.direction;
  const dir = layoutDir || settingsDir;
  return dir === READING_DIRECTION_RTL ? READING_DIRECTION_RTL : "ltr";
};

const getRtlScrollType = (manager) =>
  manager?.settings?.rtlScrollType || "default";

/**
 * Map container.scrollLeft → 1-based physical column index in reading order.
 * LTR: scrollLeft grows as you advance.
 * RTL "default": scrollLeft starts high and shrinks as you advance.
 * RTL "negative": scrollLeft starts ~0 and goes more negative as you advance.
 */
const getPhysicalPageFromScroll = (rendition, container, delta, total) => {
  if (!container || !(delta > 0) || !(total >= 1)) return null;

  const isRtl = getEpubReadingDirection(rendition) === READING_DIRECTION_RTL;
  if (!isRtl) {
    return Math.round(container.scrollLeft / delta) + 1;
  }

  const maxIndex = Math.max(0, total - 1);
  const rtlScrollType = getRtlScrollType(rendition?.manager);

  if (rtlScrollType === "default") {
    // High scrollLeft = start of chapter; advancing decreases scrollLeft.
    const indexFromStart = Math.max(
      0,
      maxIndex - Math.round(container.scrollLeft / delta),
    );
    return clampNumber(indexFromStart + 1, 1, total);
  }

  // Negative RTL scroll (Chrome 85+, Firefox, Safari): advance → more negative.
  const indexFromStart = Math.round(Math.abs(container.scrollLeft) / delta);
  return clampNumber(indexFromStart + 1, 1, total);
};

const getSyntheticPageFromLocation = (
  chapter,
  rendition,
  location,
  edge = "start",
) => {
  const { startPage, endPage, pageCount } = getChapterPageBounds(chapter);
  if (pageCount <= 1) return startPage;

  let physicalPage = null;
  let total = null;

  // Extract true physical layout geometry instead of EPUB.js potentially unsynced location.displayed properties
  const manager = rendition?.manager;
  const container = manager?.container;
  const delta = manager?.layout?.delta || container?.offsetWidth;

  if (container && delta > 0) {
    total = Math.round(container.scrollWidth / delta);
    physicalPage = getPhysicalPageFromScroll(
      rendition,
      container,
      delta,
      total,
    );
  } else {
    // Fallback if layout geometry is unavailable (e.g. before initial paint)
    let displayed = null;
    if (edge === "end") {
      displayed = location?.end?.displayed || location?.displayed || null;
    } else {
      displayed = location?.start?.displayed || location?.displayed || null;
    }
    physicalPage = Number(displayed?.page);
    total = Number(displayed?.total);
  }

  let offset;
  if (Number.isFinite(physicalPage) && Number.isFinite(total) && total >= 1) {
    if (edge === "end") {
      offset = Math.max(0, Math.ceil((physicalPage * pageCount) / total) - 1);
    } else {
      offset = Math.min(
        pageCount - 1,
        Math.floor(((physicalPage - 1) * pageCount) / total),
      );
    }
  } else {
    // Ultimate fallback relying purely on location.atEnd
    const progress = location?.atEnd ? 1 : 0;
    if (edge === "end") {
      offset = Math.max(0, Math.ceil(progress * pageCount) - 1);
    } else {
      offset = Math.min(pageCount - 1, Math.floor(progress * pageCount));
    }
  }

  return clampNumber(startPage + offset, startPage, endPage);
};

const getChapterByNumber = (metadata, chapterNumber) => {
  if (!Array.isArray(metadata?.chapters)) return null;
  return (
    metadata.chapters.find(
      (chapter) => Number(chapter.chapter_number) === Number(chapterNumber),
    ) || null
  );
};

const locationPageChanged = (before, after) => {
  const pageBefore = getDisplayedPage(before);
  const pageAfter = getDisplayedPage(after);
  return (
    typeof pageBefore === "number" &&
    typeof pageAfter === "number" &&
    pageBefore !== pageAfter
  );
};

const locationAdvanced = (before, after) => {
  const cfiBefore = getLocationCfi(before);
  const cfiAfter = getLocationCfi(after);
  if (cfiBefore && cfiAfter && cfiBefore !== cfiAfter) {
    return true;
  }
  return locationPageChanged(before, after);
};

/**
 * epub.js next()/prev() can no-op when scrollLeft+viewport is rounded past
 * scrollWidth even though another column remains. Nudge one page width when the
 * iframe can still scroll. RTL uses browser-specific scrollLeft semantics.
 */
const attemptPaginatedStep = async (rendition, direction) => {
  const isNext = direction === "next";
  const manager = rendition.manager;
  const container = manager?.container;
  const scrollBefore = container?.scrollLeft ?? 0;
  const isRtl = getEpubReadingDirection(rendition) === READING_DIRECTION_RTL;
  const rtlScrollType = getRtlScrollType(manager);

  const locBefore = rendition.currentLocation();

  if (isNext) {
    await rendition.next();
  } else {
    await rendition.prev();
  }

  const locAfter = rendition.currentLocation();
  if (locationAdvanced(locBefore, locAfter)) {
    return true;
  }

  const layout = manager?.layout;
  if (!manager?.isPaginated || !container || !layout?.delta) {
    return false;
  }

  const maxScroll = Math.max(
    0,
    container.scrollWidth - container.offsetWidth,
  );
  const scrollLeft = container.scrollLeft;

  if (isRtl) {
    // Match epub.js default manager RTL paging edge checks.
    if (rtlScrollType === "default") {
      if (isNext) {
        if (scrollLeft <= EPUB_SCROLL_EPSILON_PX) return false;
      } else if (scrollLeft >= maxScroll - EPUB_SCROLL_EPSILON_PX) {
        return false;
      }
    } else if (isNext) {
      if (scrollLeft <= -maxScroll + EPUB_SCROLL_EPSILON_PX) return false;
    } else if (scrollLeft >= -EPUB_SCROLL_EPSILON_PX) {
      return false;
    }
    manager.scrollBy(isNext ? layout.delta : -layout.delta, 0, true);
  } else if (isNext) {
    if (scrollLeft >= maxScroll - EPUB_SCROLL_EPSILON_PX) {
      return false;
    }
    manager.scrollBy(layout.delta, 0, true);
  } else if (scrollLeft <= EPUB_SCROLL_EPSILON_PX) {
    return false;
  } else {
    manager.scrollBy(-layout.delta, 0, true);
  }

  await new Promise((resolve) => requestAnimationFrame(resolve));
  if (typeof rendition.reportLocation === "function") {
    rendition.reportLocation();
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  const locRetry = rendition.currentLocation();
  if (locationAdvanced(locBefore, locRetry)) {
    return true;
  }

  const scrollAfter = container.scrollLeft ?? 0;
  return Math.abs(scrollAfter - scrollBefore) > EPUB_SCROLL_EPSILON_PX;
};

/** Reconcile epub.js atStart/atEnd with the iframe scroll position after resizes. */
const deriveChapterBoundsFromLocation = (rendition, location) => {
  let atStart = location?.atStart ?? false;
  let atEnd = location?.atEnd ?? false;

  const page = getDisplayedPage(location);
  const total = location?.end?.displayed?.total;
  if (
    atEnd &&
    typeof page === "number" &&
    typeof total === "number" &&
    page < total
  ) {
    atEnd = false;
  }

  const manager = rendition?.manager;
  const container = manager?.container;
  if (!manager?.isPaginated || !container) {
    return { atStart, atEnd };
  }

  const maxScroll = Math.max(0, container.scrollWidth - container.offsetWidth);
  const isRtl = getEpubReadingDirection(rendition) === READING_DIRECTION_RTL;
  const rtlScrollType = getRtlScrollType(manager);

  if (isRtl) {
    if (rtlScrollType === "default") {
      if (atEnd && container.scrollLeft > EPUB_SCROLL_EPSILON_PX) {
        atEnd = false;
      }
      if (
        atStart &&
        container.scrollLeft < maxScroll - EPUB_SCROLL_EPSILON_PX
      ) {
        atStart = false;
      }
    } else {
      if (
        atEnd &&
        container.scrollLeft > -maxScroll + EPUB_SCROLL_EPSILON_PX
      ) {
        atEnd = false;
      }
      if (atStart && container.scrollLeft < -EPUB_SCROLL_EPSILON_PX) {
        atStart = false;
      }
    }
  } else {
    if (atEnd && container.scrollLeft < maxScroll - EPUB_SCROLL_EPSILON_PX) {
      atEnd = false;
    }
    if (atStart && container.scrollLeft > EPUB_SCROLL_EPSILON_PX) {
      atStart = false;
    }
  }

  return { atStart, atEnd };
};

/**
 * Resolve epub.js spread for single/double page, honoring DRM cover isolation.
 * When viewing_rules.first_page is "cover" and we're on page 1 in double mode,
 * force spread "none" so the cover is not paired with the next page.
 */
const resolveEpubSpreadValue = (
  allowDoublePage,
  pageViewMode,
  drmPolicy,
  pageNumber,
) => {
  const wantsDouble = allowDoublePage && pageViewMode === "double";
  if (!wantsDouble) return "none";
  if (
    shouldIsolateCoverInDoublePage(drmPolicy) &&
    Number(pageNumber) <= 1
  ) {
    return "none";
  }
  return "auto";
};

const seekRenditionToSyntheticPage = async (
  rendition,
  chapter,
  targetPage,
  seekingRef,
) => {
  if (!rendition?.book || !chapter) return;

  // Suppress relocated page-state updates while we position the scroll.
  // display(0) fires a premature relocated event with scrollLeft=0 that would
  // overwrite the page state with the chapter's first page.
  if (seekingRef) seekingRef.current = true;

  try {
    await rendition.display(0);
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const manager = rendition.manager;
    const container = manager?.container;
    if (!manager?.isPaginated || !container) return;

    const delta = manager?.layout?.delta || container.offsetWidth;
    if (!delta || delta <= 0) return;

    const isRtl = getEpubReadingDirection(rendition) === READING_DIRECTION_RTL;
    const rtlScrollType = getRtlScrollType(manager);
    const total = Math.round(container.scrollWidth / delta);
    const maxScroll = Math.max(0, (total - 1) * delta);

    const scrollToPageIndex = (pIndex) => {
      if (!isRtl) {
        container.scrollLeft = pIndex * delta;
        return;
      }
      // epub.js RTL: "default" starts at high scrollLeft; "negative" goes left of 0.
      if (rtlScrollType === "default") {
        container.scrollLeft = Math.max(0, maxScroll - pIndex * delta);
      } else {
        container.scrollLeft = -pIndex * delta;
      }
    };

    if (total <= 1) {
      scrollToPageIndex(0);
    } else {
      const { startPage, pageCount } = getChapterPageBounds(chapter);
      if (pageCount <= 1) {
        scrollToPageIndex(0);
      } else {
        // Invert getSyntheticPageFromLocation: pick the *last* physical column
        // whose displayed start–end range covers targetPage. Nested floor()
        // (old formula) is not invertible and landed one column early
        // (e.g. seek(16) → 14–16 while the user had been viewing 16–18).
        const targetS = clampNumber(targetPage - startPage, 0, pageCount - 1);
        let pIndex = clampNumber(
          Math.floor((targetS * total) / pageCount),
          0,
          total - 1,
        );
        for (let i = 0; i < total; i += 1) {
          const startOffset = Math.min(
            pageCount - 1,
            Math.floor((i * pageCount) / total),
          );
          const endOffset = Math.max(
            0,
            Math.ceil(((i + 1) * pageCount) / total) - 1,
          );
          if (startOffset <= targetS && targetS <= endOffset) {
            pIndex = i;
          }
        }
        scrollToPageIndex(pIndex);
      }
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
  } finally {
    // Clear the seeking flag BEFORE reportLocation so the final relocated
    // event is processed by the handler with correct geometry.
    if (seekingRef) seekingRef.current = false;
  }

  if (typeof rendition.reportLocation === "function") {
    rendition.reportLocation();
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
};

/**
 * Navigate to an annotation CFI (or fall back to synthetic-page seek).
 * Used by notebook jumps so we land on the exact highlight/underline column.
 */
const displayCfiOrSeekToSyntheticPage = async (
  rendition,
  chapter,
  targetPage,
  seekingRef,
  pendingCfiRef,
) => {
  const pendingCfi = pendingCfiRef?.current;
  if (pendingCfi && rendition && typeof rendition.display === "function") {
    if (pendingCfiRef) pendingCfiRef.current = null;
    if (seekingRef) seekingRef.current = true;
    try {
      await rendition.display(pendingCfi);
      await new Promise((resolve) => requestAnimationFrame(resolve));
    } catch (err) {
      console.warn(
        "[EPUB] display(cfi) failed; falling back to synthetic seek:",
        err,
      );
      if (seekingRef) seekingRef.current = false;
      await seekRenditionToSyntheticPage(
        rendition,
        chapter,
        targetPage,
        seekingRef,
      );
      return;
    } finally {
      if (seekingRef) seekingRef.current = false;
    }
    if (typeof rendition.reportLocation === "function") {
      rendition.reportLocation();
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    return;
  }

  await seekRenditionToSyntheticPage(
    rendition,
    chapter,
    targetPage,
    seekingRef,
  );
};

const EpubViewerSimple = () => {
  const { t } = useTranslation();
  const { translationState, closeTranslationPopover, runTranslation } =
    useSelectionTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTouchTablet = useIsTouchTablet();

  // Double page mode requires sufficient width (>1024px)
  // This explicitly excludes ALL tablets (like iPads, even in landscape) and small desktops
  const allowDoublePage = !isMobile && !isTablet && !isTouchTablet;
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const renditionRef = useRef(null);
  const mountIdRef = useRef(Symbol());
  const isInitializedRef = useRef(false);
  const lastAppliedReadingDirectionRef = useRef(null);
  const didApplyDesktopDefaultViewModeRef = useRef(false);
  const scaleRef = useRef(1.0);
  const themeRef = useRef("dark");
  const epubFontFamilyRef = useRef("original");
  const epubLineHeightRef = useRef(EPUB_LINE_HEIGHT_DEFAULT);
  const lastEpubThemeApplyKeyRef = useRef("");
  const applyCurrentEpubThemeRef = useRef(null);
  const isFixedLayoutRef = useRef(false);
  const highlightEnabledRef = useRef(false);
  const underlineEnabledRef = useRef(false);
  const notesEnabledRef = useRef(false);
  const isMobileRef = useRef(isMobile);
  const [isRendering, setIsRendering] = useState(false);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const currentChunkIndexRef = useRef(0);
  const isRenderingRef = useRef(false);
  const [containerKey, setContainerKey] = useState(0);
  // State for tracking if we're at the start or end of current chapter for page navigation
  const [atChapterStart, setAtChapterStart] = useState(true);
  const [atChapterEnd, setAtChapterEnd] = useState(false);
  const atChapterStartRef = useRef(true);
  const atChapterEndRef = useRef(false);
  const [notes, setNotes] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [isNoteSaving, setIsNoteSaving] = useState(false);
  const [, setNotePosition] = useState({ x: 0, y: 0 });
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const sessionExpiredNotifiedRef = useRef(false);
  const [isFixedLayout, setIsFixedLayout] = useState(false);

  const notesFromStore = useReaderStore((s) => s.annotations.notes.data);
  const highlightsFromStore = useReaderStore(
    (s) => s.annotations.highlights.data,
  );
  const underlinesFromStore = useReaderStore(
    (s) => s.annotations.underlines.data,
  );

  // Context menu state
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectionRect, setSelectionRect] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const [expandedSelectedText, setExpandedSelectedText] = useState(false);
  const [imageLightbox, setImageLightbox] = useState({
    isOpen: false,
    src: "",
    alt: "",
  });
  const imageLightboxCloseButtonRef = useRef(null);
  const epubAnnotationRefreshRafRef = useRef(null);

  // Selected EPUB annotations rendered with an out-of-iframe bounding box.
  const [selectedHighlightOverlay, setSelectedHighlightOverlay] =
    useState(null);
  const [selectedUnderlineOverlay, setSelectedUnderlineOverlay] =
    useState(null);

  const contextMenuVisibleRef = useRef(false);
  const suppressAnnotationMenuCloseUntilRef = useRef(0);
  const contextMenuRangeRef = useRef(null); // Store DOM range for context menu actions
  const contextMenuCfiRangeRef = useRef(null); // Store CFI range for context menu highlight
  const contextMenuContentsRef = useRef(null); // Store contents for context menu highlight
  const handleHighlightClickRef = useRef(null);
  const handleUnderlineClickRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const undoRedoInProgressRef = useRef(false);
  const lastUndoRedoRunRef = useRef({ undo: 0, redo: 0 });
  const UNDO_REDO_DEBOUNCE_MS = 550;
  const [, setUndoRedoVersion] = useState(0);

  // Map highlight hex color to EPUB highlight class (single token; epub.js uses classList.add)
  const getEpubHighlightColorClass = useCallback((hex) => {
    const map = {
      "#F28B82": "red",
      "#F7B88A": "orange",
      "#FFF59D": "yellow",
      "#A5D6A7": "green",
      "#90CAF9": "light-blue",
      "#CE93D8": "purple",
    };
    const normalized = (hex || "").trim().toUpperCase();
    const key = map[normalized] || "yellow";
    return `epub-highlight-${key}`;
  }, []);
  const getEpubHighlightStyles = useCallback((color) => {
    return {
      fill: color || "#FFEB3B",
      "fill-opacity": EPUB_HIGHLIGHT_OPACITY,
    };
  }, []);
  const getHighlightCfiRange = useCallback((annotation) => {
    const coordinates = annotation?.coordinates;
    if (!coordinates) return null;
    return (
      coordinates.cfiRange ??
      coordinates.cfi_range ??
      (Array.isArray(coordinates)
        ? (coordinates[0]?.cfiRange ?? coordinates[0]?.cfi_range)
        : null)
    );
  }, []);
  const isPageWithinChapterRange = useCallback((pageNumber, chapterNumber) => {
    const currentMetadata = useReaderStore.getState().metadata;
    if (!currentMetadata || !Array.isArray(currentMetadata.chapters)) {
      return true;
    }
    const page = Number(pageNumber);
    if (Number.isNaN(page)) return false;
    const chapter = currentMetadata.chapters.find(
      (c) => Number(c.chapter_number) === Number(chapterNumber),
    );
    if (!chapter) return false;
    const startPage = Number(chapter.start_page) || 1;
    const endPage = Number(chapter.end_page) || startPage;
    return page >= startPage && page <= endPage;
  }, []);
  const getHighlightsForChapter = useCallback(
    (chapterNumber) => {
      const chunkNum = Number(chapterNumber);
      const currentHighlights =
        useReaderStore.getState().annotations?.highlights?.data || [];
      return currentHighlights.filter((h) => {
        const hChapter = Number(h.chapter_number);
        if (h.chapter_number !== undefined && !Number.isNaN(hChapter)) {
          return hChapter === chunkNum;
        }
        const hPage = Number(h.page_number);
        return (
          !Number.isNaN(hPage) && isPageWithinChapterRange(hPage, chunkNum)
        );
      });
    },
    [isPageWithinChapterRange],
  );

  const getRangeFromCfi = useCallback((contents, cfiRange) => {
    if (!contents?.range || !cfiRange) return null;
    try {
      return contents.range(cfiRange);
    } catch {
      return null;
    }
  }, []);

  const rangesOverlap = useCallback((firstRange, secondRange) => {
    if (!firstRange || !secondRange) return false;
    try {
      return (
        firstRange.compareBoundaryPoints(3, secondRange) > 0 &&
        firstRange.compareBoundaryPoints(1, secondRange) < 0
      );
    } catch {
      return false;
    }
  }, []);

  const rangeContains = useCallback((outerRange, innerRange) => {
    if (!outerRange || !innerRange) return false;
    try {
      return (
        outerRange.compareBoundaryPoints(0, innerRange) <= 0 &&
        outerRange.compareBoundaryPoints(2, innerRange) >= 0
      );
    } catch {
      return false;
    }
  }, []);

  const getSimpleCfiBounds = useCallback((cfiRange) => {
    if (!cfiRange) return null;
    const match = String(cfiRange).match(/\/1:(\d+),\/1:(\d+)\)/);
    if (!match) return null;
    const start = Number.parseInt(match[1], 10);
    const end = Number.parseInt(match[2], 10);
    if (Number.isNaN(start) || Number.isNaN(end)) return null;
    return { start, end };
  }, []);

  const cfiRangesOverlap = useCallback(
    (newCfiRange, existingCfiRange) => {
      const newBounds = getSimpleCfiBounds(newCfiRange);
      const existingBounds = getSimpleCfiBounds(existingCfiRange);
      if (!newBounds || !existingBounds) return false;
      return (
        newBounds.start < existingBounds.end &&
        newBounds.end > existingBounds.start
      );
    },
    [getSimpleCfiBounds],
  );

  const getUnderlineCfiBounds = useCallback((cfiRange) => {
    if (!cfiRange) return null;
    const match = String(cfiRange).match(/^(.*)\/1:(\d+),\/1:(\d+)\)$/);
    if (!match) return null;
    const prefix = match[1];
    const start = Number.parseInt(match[2], 10);
    const end = Number.parseInt(match[3], 10);
    if (!prefix || Number.isNaN(start) || Number.isNaN(end)) return null;
    return { prefix, start, end };
  }, []);

  const underlineCfiRangesOverlap = useCallback(
    (newCfiRange, existingCfiRange) => {
      const newBounds = getUnderlineCfiBounds(newCfiRange);
      const existingBounds = getUnderlineCfiBounds(existingCfiRange);
      if (!newBounds || !existingBounds) return false;
      if (newBounds.prefix !== existingBounds.prefix) return false;
      return (
        newBounds.start < existingBounds.end &&
        newBounds.end > existingBounds.start
      );
    },
    [getUnderlineCfiBounds],
  );

  const underlineCoversSelection = useCallback(
    (selectionRange, selectionCfiRange, contents, existingCfiRange) => {
      const existingRange = getRangeFromCfi(contents, existingCfiRange);
      if (selectionRange && existingRange) {
        return rangeContains(existingRange, selectionRange);
      }

      const selectionBounds = getUnderlineCfiBounds(selectionCfiRange);
      const existingBounds = getUnderlineCfiBounds(existingCfiRange);
      if (!selectionBounds || !existingBounds) return false;
      if (selectionBounds.prefix !== existingBounds.prefix) return false;
      return (
        existingBounds.start <= selectionBounds.start &&
        existingBounds.end >= selectionBounds.end
      );
    },
    [getRangeFromCfi, getUnderlineCfiBounds, rangeContains],
  );

  const existingCoversSelection = useCallback(
    (selectionRange, selectionCfiRange, contents, existingCfiRange) => {
      const existingRange = getRangeFromCfi(contents, existingCfiRange);
      if (selectionRange && existingRange) {
        return rangeContains(existingRange, selectionRange);
      }

      const selectionBounds = getSimpleCfiBounds(selectionCfiRange);
      const existingBounds = getSimpleCfiBounds(existingCfiRange);
      if (!selectionBounds || !existingBounds) return false;
      return (
        existingBounds.start <= selectionBounds.start &&
        existingBounds.end >= selectionBounds.end
      );
    },
    [getRangeFromCfi, getSimpleCfiBounds, rangeContains],
  );

  const mergeRanges = useCallback((firstRange, secondRange) => {
    if (!firstRange) return secondRange?.cloneRange?.() || secondRange || null;
    if (!secondRange) return firstRange?.cloneRange?.() || firstRange || null;

    try {
      const mergedRange = firstRange.cloneRange();
      if (firstRange.compareBoundaryPoints(0, secondRange) > 0) {
        mergedRange.setStart(
          secondRange.startContainer,
          secondRange.startOffset,
        );
      }
      if (firstRange.compareBoundaryPoints(2, secondRange) < 0) {
        mergedRange.setEnd(secondRange.endContainer, secondRange.endOffset);
      }
      return mergedRange;
    } catch {
      return firstRange;
    }
  }, []);

  const getMergedHighlightsForView = useCallback(
    (view, highlights) => {
      const contents = view?.contents;
      if (!contents || !Array.isArray(highlights) || highlights.length === 0) {
        return [];
      }

      const items = highlights
        .map((highlight) => {
          const cfiRange = highlight.coordinates?.cfiRange;
          if (!cfiRange) return null;
          const range = getRangeFromCfi(contents, cfiRange);
          if (!range) return null;
          return { highlight, range };
        })
        .filter(Boolean)
        .sort((a, b) => {
          try {
            return a.range.compareBoundaryPoints(0, b.range);
          } catch {
            return 0;
          }
        });

      const merged = [];
      items.forEach((item) => {
        const last = merged[merged.length - 1];
        const overlapsLast =
          last &&
          (rangesOverlap(last.range, item.range) ||
            rangeContains(last.range, item.range) ||
            rangeContains(item.range, last.range));

        if (overlapsLast) {
          last.range = mergeRanges(last.range, item.range);
          last.highlight = item.highlight;
          return;
        }

        merged.push({
          range: item.range,
          highlight: item.highlight,
        });
      });

      return merged;
    },
    [getRangeFromCfi, mergeRanges, rangeContains, rangesOverlap],
  );

  const getMergedSelectionCluster = useCallback(
    (selectionRange, selectionCfiRange, contents, annotations) => {
      if (
        !selectionRange ||
        !selectionCfiRange ||
        !Array.isArray(annotations) ||
        annotations.length === 0
      ) {
        return {
          overlappingAnnotations: [],
          mergedRange: selectionRange || null,
          mergedCfiRange: selectionCfiRange || null,
          mergedContent: selectionRange ? selectionRange.toString().trim() : "",
        };
      }

      const overlappingAnnotations = [];
      let mergedRange = selectionRange.cloneRange();

      annotations.forEach((annotation) => {
        const existingCfi = annotation.coordinates?.cfiRange;
        if (!existingCfi) return;

        const existingRange = getRangeFromCfi(contents, existingCfi);
        const overlaps = existingRange
          ? rangesOverlap(selectionRange, existingRange) ||
            rangeContains(selectionRange, existingRange) ||
            rangeContains(existingRange, selectionRange)
          : cfiRangesOverlap(selectionCfiRange, existingCfi);

        if (!overlaps) return;

        overlappingAnnotations.push(annotation);
        if (existingRange) {
          mergedRange = mergeRanges(mergedRange, existingRange);
        }
      });

      return {
        overlappingAnnotations,
        mergedRange,
        mergedCfiRange:
          contents?.cfiFromRange?.(mergedRange) || selectionCfiRange,
        mergedContent: mergedRange ? mergedRange.toString().trim() : "",
      };
    },
    [
      cfiRangesOverlap,
      getRangeFromCfi,
      mergeRanges,
      rangeContains,
      rangesOverlap,
    ],
  );

  // Find underlines that overlap with the new selection and should be deleted.
  // If the new selection is fully contained, keep the existing underline.
  const findUnderlinesToDelete = useCallback(
    (newCfiRange, selectionRange, contents, underlinesFromStore) => {
      if (!newCfiRange || !Array.isArray(underlinesFromStore)) return [];

      try {
        const toDelete = [];

        // Match highlights' strategy: prefer actual DOM ranges, then fall back
        // to a structured CFI comparison when ranges cannot be resolved.
        underlinesFromStore.forEach((underline) => {
          const existingCfi = underline.coordinates?.cfiRange;
          if (!existingCfi) return;

          const existingRange = getRangeFromCfi(contents, existingCfi);
          const overlaps = existingRange
            ? Boolean(
                selectionRange && rangesOverlap(selectionRange, existingRange),
              ) ||
              rangeContains(selectionRange, existingRange) ||
              rangeContains(existingRange, selectionRange)
            : underlineCfiRangesOverlap(newCfiRange, existingCfi);

          if (!overlaps) return;

          if (
            underlineCoversSelection(
              selectionRange,
              newCfiRange,
              contents,
              existingCfi,
            )
          ) {
            return;
          }

          console.info(
            "[EPUB Underlines] Deleting old underline (overlap replacement)",
            {
              newCfiRange,
              existingCfi,
              id: underline.id,
            },
          );
          toDelete.push(underline);
        });

        return toDelete;
      } catch (err) {
        console.warn("[EPUB Underlines] Delete detection error:", err);
        return [];
      }
    },
    [
      getRangeFromCfi,
      rangeContains,
      rangesOverlap,
      underlineCfiRangesOverlap,
      underlineCoversSelection,
    ],
  );

  async function applyEpubHighlightSelection({
    selectionRange,
    selectionCfiRange,
    contents,
    selectedTextStr,
  }) {
    if (undoRedoInProgressRef.current) return false;
    if (!contents) return false;

    try {
      setSelectionRect(null);

      const effectiveRange =
        selectionRange ||
        (selectionCfiRange
          ? getRangeFromCfi(contents, selectionCfiRange)
          : null);
      const effectiveCfiRange =
        selectionCfiRange ||
        (effectiveRange ? contents.cfiFromRange(effectiveRange) : null);
      const effectiveContent = (
        selectedTextStr ||
        effectiveRange?.toString?.() ||
        ""
      ).trim();

      if (!effectiveRange || !effectiveCfiRange || !effectiveContent) {
        return false;
      }

      const chapterNum = currentChunkIndexRef.current ?? 1;
      const pageNum = currentPageRef.current ?? 1;
      const currentHighlights = getHighlightsForChapter(chapterNum);
      const { overlappingAnnotations, mergedCfiRange, mergedContent } =
        getMergedSelectionCluster(
          effectiveRange,
          effectiveCfiRange,
          contents,
          currentHighlights,
        );

      const isCovered = overlappingAnnotations.some((h) => {
        const existingCfi = h.coordinates?.cfiRange;
        if (!existingCfi) return false;
        return existingCoversSelection(
          effectiveRange,
          effectiveCfiRange,
          contents,
          existingCfi,
        );
      });

      if (isCovered) {
        console.info(
          "[EPUB Highlights] Skipping - new selection is fully covered by existing highlight",
        );
        contents.window.getSelection()?.removeAllRanges();
        return false;
      }

      for (const oldHighlight of overlappingAnnotations) {
        const oldCfi = oldHighlight.coordinates?.cfiRange;
        if (oldCfi && renditionRef.current) {
          try {
            renditionRef.current.annotations.remove(oldCfi, "highlight");
          } catch (err) {
            console.warn("[EPUB Highlights] Error removing from DOM:", err);
          }
        }

        if (oldHighlight.id) {
          try {
            removeHighlightById(oldHighlight.id);
          } catch (err) {
            console.warn(
              "[EPUB Highlights] Error removing from API:",
              oldHighlight.id,
              err,
            );
          }
        }
      }

      const finalCfiRange = mergedCfiRange || effectiveCfiRange;
      const finalContent = mergedContent || effectiveContent;
      const currentColor = useReaderStore.getState().highlightColor;
      const colorClass = getEpubHighlightColorClass(currentColor);
      const highlightStyles = getEpubHighlightStyles(currentColor || "#FFF59D");

      renditionRef.current?.annotations.add(
        "highlight",
        finalCfiRange,
        {},
        (e) => handleHighlightClick(finalCfiRange, null, finalContent || "", e),
        colorClass,
        highlightStyles,
      );
      contents.window.getSelection()?.removeAllRanges();

      const actionPayload = {
        cfiRange: finalCfiRange,
        content: finalContent,
        color: currentColor,
        page_number: pageNum,
        chapter_number: chapterNum,
        wasUndone: false,
      };
      const highlightAction = {
        type: "add-highlight",
        payload: actionPayload,
      };
      const lastAction = undoStackRef.current.at(-1);
      const shouldCollapseHistory =
        overlappingAnnotations.length > 0 &&
        lastAction?.type === "add-highlight" &&
        Number(lastAction.payload?.chapter_number) === Number(chapterNum) &&
        String(lastAction.payload?.color || "") ===
          String(currentColor || "") &&
        lastAction.payload?.cfiRange &&
        cfiRangesOverlap(
          String(lastAction.payload.cfiRange),
          String(finalCfiRange),
        );

      if (shouldCollapseHistory) {
        undoStackRef.current[undoStackRef.current.length - 1] = {
          ...lastAction,
          pageNumber: pageNum,
          ...highlightAction,
        };
        redoStackRef.current = [];
        emitUndoRedoCapabilities();
        syncUndoRedoToCache();
      } else {
        pushAction(pageNum, highlightAction);
      }

      try {
        const created = createHighlight({
          page_number: pageNum,
          content: finalContent,
          color: currentColor,
          coordinates: { cfiRange: finalCfiRange },
          chapter_number: chapterNum,
        });
        if (created?.id) {
          actionPayload.highlightId = created.id;
          if (actionPayload.wasUndone) {
            try {
              removeHighlightById(created.id);
            } catch (err) {
              console.error(
                "Error deleting late-created highlight after undo:",
                err,
              );
            }
          }
        }
      } catch (err) {
        console.error("Error adding highlight:", err);
        return false;
      }

      return true;
    } catch (err) {
      console.error("Error adding highlight:", err);
      return false;
    }
  }

  async function applyEpubUnderlineSelection({
    selectionRange,
    selectionCfiRange,
    contents,
    selectedTextStr,
  }) {
    if (undoRedoInProgressRef.current) return false;
    if (!contents) return false;

    try {
      setSelectionRect(null);
      const effectiveRange =
        selectionRange ||
        (selectionCfiRange
          ? getRangeFromCfi(contents, selectionCfiRange)
          : null);
      const effectiveCfiRange =
        selectionCfiRange ||
        (effectiveRange ? contents.cfiFromRange(effectiveRange) : null);
      const effectiveContent = (
        selectedTextStr ||
        effectiveRange?.toString?.() ||
        ""
      ).trim();

      if (!effectiveRange || !effectiveCfiRange || !effectiveContent) {
        return false;
      }

      const currentUnderlines =
        useReaderStore.getState().annotations.underlines.data || [];
      const isCovered =
        Array.isArray(currentUnderlines) &&
        currentUnderlines.some((u) => {
          const existingCfi = u.coordinates?.cfiRange;
          if (!existingCfi) return false;
          return underlineCoversSelection(
            effectiveRange,
            effectiveCfiRange,
            contents,
            existingCfi,
          );
        });

      if (isCovered) {
        console.info(
          "[EPUB Underlines] Skipping - new selection is fully covered by existing underline",
        );
        contents.window.getSelection()?.removeAllRanges();
        return false;
      }

      const underlinesToDelete = findUnderlinesToDelete(
        effectiveCfiRange,
        effectiveRange,
        contents,
        Array.isArray(currentUnderlines) ? currentUnderlines : [],
      );

      for (const oldUnderline of underlinesToDelete) {
        const oldCfi = oldUnderline.coordinates?.cfiRange;
        if (oldCfi && renditionRef.current) {
          try {
            renditionRef.current.annotations.remove(oldCfi, "underline");
          } catch (err) {
            console.warn("[EPUB Underlines] Error removing from DOM:", err);
          }
        }

        if (oldUnderline.id) {
          try {
            deleteUnderline(oldUnderline.id);
          } catch (err) {
            console.warn(
              "[EPUB Underlines] Error removing from API:",
              oldUnderline.id,
              err,
            );
          }
        }
      }

      const chapterNum = currentChunkIndexRef.current ?? 1;
      const pageNum = currentPageRef.current ?? 1;
      const actionPayload = {
        cfiRange: effectiveCfiRange,
        content: effectiveContent,
        page_number: pageNum,
        chapter_number: chapterNum,
        wasUndone: false,
      };
      const underlineAction = {
        type: "add-underline",
        payload: actionPayload,
      };
      const lastAction = undoStackRef.current.at(-1);
      const shouldCollapseHistory =
        underlinesToDelete.length > 0 &&
        lastAction?.type === "add-underline" &&
        Number(lastAction.payload?.chapter_number) === Number(chapterNum) &&
        lastAction.payload?.cfiRange &&
        underlineCfiRangesOverlap(
          String(lastAction.payload.cfiRange),
          String(effectiveCfiRange),
        );

      renditionRef.current?.annotations.add(
        "underline",
        effectiveCfiRange,
        {},
        (e) =>
          handleUnderlineClick(
            effectiveCfiRange,
            null,
            effectiveContent || "",
            e,
          ),
        "epub-underline",
      );
      contents.window.getSelection()?.removeAllRanges();

      if (shouldCollapseHistory) {
        undoStackRef.current[undoStackRef.current.length - 1] = {
          ...lastAction,
          pageNumber: pageNum,
          ...underlineAction,
        };
        redoStackRef.current = [];
        emitUndoRedoCapabilities();
        syncUndoRedoToCache();
      } else {
        pushAction(pageNum, underlineAction);
      }

      const newUnderline = createUnderline({
        page_number: pageNum,
        content: effectiveContent,
        coordinates: { cfiRange: effectiveCfiRange },
        chapter_number: chapterNum,
      });

      if (newUnderline?.id) {
        actionPayload.underlineId = newUnderline.id;
        if (actionPayload.wasUndone) {
          try {
            deleteUnderline(newUnderline.id);
          } catch (err) {
            console.error(
              "Error deleting late-created underline after undo:",
              err,
            );
          }
        }
      }

      return true;
    } catch (err) {
      console.error("Error adding underline:", err);
      return false;
    }
  }

  // Keep ref in sync with state
  useEffect(() => {
    contextMenuVisibleRef.current = contextMenuVisible;
  }, [contextMenuVisible]);

  const openImageLightbox = useCallback(
    (src, alt = "") => {
      if (!src) return;
      setContextMenuVisible(false);
      setSelectedText("");
      setImageLightbox({
        isOpen: true,
        src,
        alt,
      });
    },
    [setContextMenuVisible, setSelectedText],
  );

  const closeImageLightbox = useCallback(() => {
    setImageLightbox((prev) => {
      if (!prev.isOpen) return prev;
      return {
        ...prev,
        isOpen: false,
      };
    });
  }, []);

  const getRangeBounds = useCallback((range) => {
    if (!range) return null;

    const rects = Array.from(range.getClientRects() || []).filter(
      (rect) => rect && (rect.width > 0 || rect.height > 0),
    );

    if (rects.length > 0) {
      let left = Infinity;
      let top = Infinity;
      let right = -Infinity;
      let bottom = -Infinity;

      rects.forEach((rect) => {
        left = Math.min(left, rect.left);
        top = Math.min(top, rect.top);
        right = Math.max(right, rect.right);
        bottom = Math.max(bottom, rect.bottom);
      });

      if (
        Number.isFinite(left) &&
        Number.isFinite(top) &&
        Number.isFinite(right) &&
        Number.isFinite(bottom)
      ) {
        return {
          left,
          top,
          width: Math.max(0, right - left),
          height: Math.max(0, bottom - top),
        };
      }
    }

    const rect = range.getBoundingClientRect();
    if (!rect || (!rect.width && !rect.height)) return null;
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }, []);

  const getAnnotationOverlayRect = useCallback(
    (cfiRange, clickEvent) => {
      if (!cfiRange || !renditionRef.current) return null;

      const eventRect = clickEvent?.target?.getBoundingClientRect?.();
      if (
        eventRect &&
        (eventRect.width > 0 || eventRect.height > 0) &&
        Number.isFinite(eventRect.left) &&
        Number.isFinite(eventRect.top)
      ) {
        const minWidth = Math.max(24, eventRect.width);
        const minHeight = Math.max(16, eventRect.height);
        return {
          left: eventRect.left,
          top: eventRect.top,
          width: minWidth,
          height: minHeight,
        };
      }

      const views = renditionRef.current.views?._views || [];
      for (const view of views) {
        if (!view?.contents?.range || !view?.element?.getBoundingClientRect) {
          continue;
        }
        try {
          const range = view.contents.range(cfiRange);
          const localBounds = getRangeBounds(range);
          if (!localBounds) continue;

          const iframeRect = view.element.getBoundingClientRect();
          const width = Math.max(24, localBounds.width);
          const height = Math.max(16, localBounds.height);
          return {
            left: iframeRect.left + localBounds.left,
            top: iframeRect.top + localBounds.top,
            width,
            height,
          };
        } catch {
          // Continue and try another rendered view.
        }
      }

      return null;
    },
    [getRangeBounds],
  );

  const refreshRenderedEpubAnnotations = useCallback(() => {
    const rendition = renditionRef.current;
    if (!rendition?.views) return;

    const viewCollection = rendition.views();
    const renderedViews = viewCollection?._views || [];
    if (renderedViews.length === 0) return;

    const refreshMarkSet = (view, marks) => {
      if (!view?.placeMark || !marks) return;
      Object.values(marks).forEach((item) => {
        const mark = item?.mark;
        const element = mark?.element || item?.element;
        const range = mark?.range;
        if (!element || !range) return;
        try {
          view.placeMark(element, range);
        } catch {}
      });
    };

    renderedViews.forEach((view) => {
      if (!view?.displayed) return;
      try {
        view.pane?.render?.();
      } catch {}
      refreshMarkSet(view, view.highlights);
      refreshMarkSet(view, view.underlines);
      refreshMarkSet(view, view.marks);
    });
  }, []);

  useEffect(() => {
    if (!imageLightbox.isOpen) return;

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        // Capture phase + preventDefault helps ensure we close even if the
        // EPUB iframe or other listeners stop propagation.
        event.preventDefault();
        event.stopPropagation();
        closeImageLightbox();
      }
    };

    document.addEventListener("keydown", handleEscKey, true);
    return () => {
      document.removeEventListener("keydown", handleEscKey, true);
    };
  }, [imageLightbox.isOpen, closeImageLightbox]);

  // Help ensure the Escape key works consistently by moving focus to the
  // fullscreen overlay's close control.
  useEffect(() => {
    if (!imageLightbox.isOpen) return;
    const t = window.setTimeout(() => {
      imageLightboxCloseButtonRef.current?.focus?.();
    }, 0);
    return () => window.clearTimeout(t);
  }, [imageLightbox.isOpen]);

  // Suppress epub.js highlight errors that occur during scroll/resize
  useEffect(() => {
    const isEpubHighlightError = (message) => {
      return (
        message &&
        (message.includes("Cannot read properties of null") ||
          message.includes("Cannot read property")) &&
        (message.includes("getClientRects") ||
          message.includes("filteredRanges") ||
          message.includes("Highlight"))
      );
    };

    const handleError = (event) => {
      if (isEpubHighlightError(event.message)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return true;
      }
    };

    const handleUnhandledRejection = (event) => {
      if (
        event.reason &&
        isEpubHighlightError(event.reason.message || String(event.reason))
      ) {
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener("error", handleError, true);
    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection,
      true,
    );

    return () => {
      window.removeEventListener("error", handleError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
        true,
      );
    };
  }, []);

  const currentSectionRef = useRef(currentSection);
  const lastObservedContainerWidthRef = useRef(0);

  // Track whether the reader triggered the currentPage update to avoid flickering
  const skipCurrentPageSyncRef = useRef(false);
  const currentPageRef = useRef(null);
  // Suppress relocated page-state updates while seekRenditionToSyntheticPage
  // is in progress. display(0) fires a premature relocated with scrollLeft=0
  // that would overwrite the target page before the seek completes.
  const isSeekingRef = useRef(false);
  // When notebook jumps to a highlight/underline in another chapter, stash the
  // CFI here so the chapter-switch path can display(cfi) instead of synthetic seek.
  const pendingDisplayCfiRef = useRef(null);
  const initialViewAppliedRef = useRef(false);
  const [epubReady, setEpubReady] = useState(false);
  const [pageNavigationRequest, setPageNavigationRequest] = useState(0);

  const requestPageNavigation = useCallback(() => {
    setPageNavigationRequest((count) => count + 1);
  }, []);

  // Keep ref in sync with currentSection
  useEffect(() => {
    currentSectionRef.current = currentSection;
  }, [currentSection]);

  useEffect(() => {
    currentChunkIndexRef.current = currentChunkIndex;
  }, [currentChunkIndex]);

  useEffect(() => {
    isRenderingRef.current = isRendering;
  }, [isRendering]);

  const {
    document: epubBook,
    currentPage,
    scale,
    setScale,
    theme,
    epubFontFamily,
    epubLineHeight,
    setEpubRendition,
    setCurrentPage,
    setCurrentPageEnd,
    setCurrentPageWithEnd,
    highlightEnabled,
    underlineEnabled,
    documentType,
    documentName,
    notesEnabled,
    noteListVersion,
    setNotesEnabled,
    setSearchQuery,
    setSearchSidebarOpen,
    isSearchSidebarOpen,
    metadata: rawMetadata,
    loadedChunks,
    documentId,
    loadEpubChapter,
    drmPolicy,
    initialChapterNumber,
    setEpubCurrentChapterNumber,
    searchResults,
    readerViewMode,
    setReaderViewMode,
    pageViewMode,
    readingDirection,
    initialViewStartPage,
    clearInitialViewStartPage,
    highlightColor,
    setHighlightColor,
    openReadingAssistantAskAi,
  } = useReaderStore();

  const isRtlReading = readingDirection === READING_DIRECTION_RTL;

  // Ensure chapters are sorted by start_page for serial navigation
  const metadata = useMemo(() => {
    if (!rawMetadata || !Array.isArray(rawMetadata.chapters))
      return rawMetadata;
    const sortedChapters = [...rawMetadata.chapters].sort((a, b) => {
      const startA = Number(a.start_page) || 0;
      const startB = Number(b.start_page) || 0;
      if (startA !== startB) return startA - startB;
      return (Number(a.chapter_number) || 0) - (Number(b.chapter_number) || 0);
    });
    return { ...rawMetadata, chapters: sortedChapters };
  }, [rawMetadata]);

  // Double page mode is only available on large desktops
  const isDoublePageMode = allowDoublePage && pageViewMode === "double";
  const epubSpreadValue = resolveEpubSpreadValue(
    allowDoublePage,
    pageViewMode,
    drmPolicy,
    currentPage,
  );

  // Default EPUB view mode: desktop starts as fit-to-width (only when in single page mode).
  // Keep this as a one-time default so we don't override explicit user changes later.
  useEffect(() => {
    if (didApplyDesktopDefaultViewModeRef.current) return;
    if (isMobile) return;
    // Don't set fit-to-width default when in double page mode
    if (pageViewMode === "double") {
      didApplyDesktopDefaultViewModeRef.current = true;
      return;
    }
    if (readerViewMode !== "fit-to-page") {
      didApplyDesktopDefaultViewModeRef.current = true;
      return;
    }
    setReaderViewMode("fit-to-width");
    didApplyDesktopDefaultViewModeRef.current = true;
  }, [isMobile, readerViewMode, setReaderViewMode, pageViewMode]);

  const emitUndoRedoCapabilities = useCallback(() => {
    const canUndo = undoStackRef.current.length > 0;
    const canRedo = redoStackRef.current.length > 0;
    window.dispatchEvent(
      new CustomEvent("reader:can-undo", { detail: { canUndo, canRedo } }),
    );
    setUndoRedoVersion((v) => v + 1);
  }, []);

  const syncUndoRedoToCache = useCallback(() => {
    const key = getEpubStacksKey();
    if (!key) return;
    epubUndoRedoCache.set(key, {
      undo: [...undoStackRef.current],
      redo: [...redoStackRef.current],
    });
  }, []);

  const pushAction = useCallback(
    (pageNumber, action) => {
      const actionWithPage = { ...action, pageNumber };
      undoStackRef.current.push(actionWithPage);
      redoStackRef.current = [];
      emitUndoRedoCapabilities();
      syncUndoRedoToCache();
    },
    [emitUndoRedoCapabilities, syncUndoRedoToCache],
  );

  const syncAnnotationIdAcrossStacks = useCallback(
    ({ type, cfiRange, annotationId }) => {
      if (!type || !cfiRange || !annotationId) return;

      const idField = type === "highlight" ? "highlightId" : "underlineId";
      const addType = type === "highlight" ? "add-highlight" : "add-underline";
      const deleteType =
        type === "highlight" ? "delete-highlight" : "delete-underline";

      const updateStack = (stack) => {
        stack.forEach((entry) => {
          if (!entry?.payload) return;
          if (entry.type !== addType && entry.type !== deleteType) return;
          if (String(entry.payload.cfiRange || "") !== String(cfiRange)) return;
          entry.payload[idField] = annotationId;
        });
      };

      updateStack(undoStackRef.current);
      updateStack(redoStackRef.current);
      syncUndoRedoToCache();
    },
    [syncUndoRedoToCache],
  );

  // Restore undo/redo stacks from cache when document loads (survives remounts/HMR)
  useEffect(() => {
    if (!documentId) return;
    const key = getEpubStacksKey();
    const cached = epubUndoRedoCache.get(key);
    if (cached && (cached.undo?.length > 0 || cached.redo?.length > 0)) {
      undoStackRef.current = cached.undo;
      redoStackRef.current = cached.redo;
      emitUndoRedoCapabilities();
    }
  }, [documentId, emitUndoRedoCapabilities]);

  useEffect(() => {
    const handleUndo = async () => {
      const now = Date.now();
      if (undoRedoInProgressRef.current) return;
      if (now - lastUndoRedoRunRef.current.undo < UNDO_REDO_DEBOUNCE_MS) return;
      if (undoStackRef.current.length === 0) return;
      undoRedoInProgressRef.current = true;
      lastUndoRedoRunRef.current.undo = now;
      try {
        const action = undoStackRef.current.pop();
        if (!action) return;

        redoStackRef.current.push(action);
        emitUndoRedoCapabilities();
        syncUndoRedoToCache();
        const rendition = renditionRef.current;
        if (action.type === "add-highlight") {
          if (action.payload && typeof action.payload === "object") {
            action.payload.wasUndone = true;
          }
          const { highlightId, cfiRange } = action.payload;
          const highlights =
            useReaderStore.getState().annotations?.highlights?.data || [];
          let found = highlights.find((h) => {
            const c = h.coordinates;
            if (!c) return false;
            const cfi =
              c.cfiRange ??
              c.cfi_range ??
              (Array.isArray(c)
                ? (c[0]?.cfiRange ?? c[0]?.cfi_range)
                : undefined);
            return cfi === cfiRange;
          });
          if (
            !found &&
            action.payload.content != null &&
            action.payload.chapter_number != null
          ) {
            const byContent = highlights.filter(
              (h) =>
                (h.content || "").trim() ===
                  String(action.payload.content).trim() &&
                Number(h.chapter_number) ===
                  Number(action.payload.chapter_number),
            );
            if (byContent.length === 1) found = byContent[0];
          }
          const idToDelete = found?.id || highlightId;
          if (rendition && cfiRange) {
            try {
              rendition.annotations.remove(cfiRange, "highlight");
            } catch (e) {}
          }
          if (idToDelete) {
            removeHighlightById(idToDelete);
          }
        } else if (action.type === "add-underline") {
          if (action.payload && typeof action.payload === "object") {
            action.payload.wasUndone = true;
          }
          const {
            underlineId,
            cfiRange,
            content,
            page_number,
            chapter_number,
          } = action.payload;
          if (rendition && cfiRange) {
            try {
              rendition.annotations.remove(cfiRange, "underline");
            } catch (e) {}
          }
          // Prefer the latest underline ID from store (CFI/content match) over stale payload IDs.
          let resolvedUnderlineId = null;
          try {
            const store = useReaderStore.getState();
            const underlines = store.annotations?.underlines?.data || [];
            const foundByCfi = cfiRange
              ? underlines.find((u) => {
                  const uc = u.coordinates;
                  const ucfi = uc?.cfiRange ?? uc?.cfi_range;
                  return ucfi && String(ucfi) === String(cfiRange);
                })
              : null;
            if (foundByCfi?.id) {
              resolvedUnderlineId = foundByCfi.id;
            } else if (content != null) {
              const byContent = underlines.filter((u) => {
                if (!u.content) return false;
                if (String(u.content).trim() !== String(content).trim()) {
                  return false;
                }
                if (page_number != null && u.page_number === page_number) {
                  return true;
                }
                if (
                  chapter_number != null &&
                  u.chapter_number === chapter_number
                ) {
                  return true;
                }
                return false;
              });
              if (byContent.length === 1 && byContent[0]?.id) {
                resolvedUnderlineId = byContent[0].id;
              }
            }
          } catch (e) {
            console.error("Undo: underline id resolution failed", e);
          }
          resolvedUnderlineId = resolvedUnderlineId || underlineId;

          if (resolvedUnderlineId) {
            try {
              deleteUnderline(resolvedUnderlineId);
              devDebug(
                `[Undo] Deleted underline from API: ${resolvedUnderlineId}`,
              );
            } catch (e) {
              console.error("Undo: delete underline failed", e);
            }
          } else {
            // Fallback: search store by cfiRange or by content+page/chapter
            try {
              const store = useReaderStore.getState();
              const underlines = store.annotations?.underlines?.data || [];
              let found = null;
              if (cfiRange) {
                found = underlines.find((u) => {
                  const uc = u.coordinates;
                  const ucfi = uc?.cfiRange ?? uc?.cfi_range;
                  return ucfi && String(ucfi) === String(cfiRange);
                });
              }
              if (!found && content != null) {
                found = underlines.find((u) => {
                  if (!u.content) return false;
                  if (String(u.content).trim() !== String(content).trim())
                    return false;
                  if (page_number != null && u.page_number === page_number)
                    return true;
                  if (
                    chapter_number != null &&
                    u.chapter_number === chapter_number
                  )
                    return true;
                  return false;
                });
              }
              if (found && found.id) {
                try {
                  deleteUnderline(found.id);
                } catch (e) {
                  console.error("Undo: delete underline (fallback) failed", e);
                }
              }
            } catch (e) {
              console.error("Undo: underline fallback search failed", e);
            }
          }
        } else if (action.type === "delete-highlight") {
          const {
            cfiRange,
            content,
            color,
            page_number,
            chapter_number,
            highlightId,
          } = action.payload || {};
          const colorClass = getEpubHighlightColorClass(color || "#FFF59D");
          const highlightStyles = getEpubHighlightStyles(color || "#FFF59D");
          if (rendition && cfiRange) {
            try {
              rendition.annotations.add(
                "highlight",
                cfiRange,
                { id: highlightId || null },
                (e) =>
                  handleHighlightClickRef.current?.(
                    cfiRange,
                    action?.payload?.highlightId || highlightId || null,
                    content || "",
                    e,
                  ),
                colorClass,
                highlightStyles,
              );
            } catch (e) {}
          }
          try {
            const created = createHighlight({
              id: highlightId,
              page_number,
              content,
              color: color || "#FFF59D",
              coordinates: { cfiRange },
              chapter_number,
            });

            if (created?.id) {
              action.payload.highlightId = created.id;
              syncAnnotationIdAcrossStacks({
                type: "highlight",
                cfiRange,
                annotationId: created.id,
              });
            }
          } catch (e) {
            console.error("Undo: recreate highlight failed", e);
          }
        } else if (action.type === "delete-underline") {
          const {
            cfiRange,
            content,
            page_number,
            chapter_number,
            underlineId,
          } = action.payload || {};
          if (rendition && cfiRange) {
            try {
              rendition.annotations.add(
                "underline",
                cfiRange,
                { id: underlineId || null },
                (e) =>
                  handleUnderlineClickRef.current?.(
                    cfiRange,
                    action?.payload?.underlineId || underlineId || null,
                    content || "",
                    e,
                  ),
                "epub-underline",
              );
            } catch (e) {}
          }
          try {
            const newUnderline = createUnderline({
              id: underlineId,
              page_number,
              content,
              coordinates: { cfiRange },
              chapter_number,
            });

            if (newUnderline?.id) {
              action.payload.underlineId = newUnderline.id;
              syncAnnotationIdAcrossStacks({
                type: "underline",
                cfiRange,
                annotationId: newUnderline.id,
              });
            }
          } catch (e) {
            console.error("Undo: recreate underline failed", e);
          }
        }
        emitUndoRedoCapabilities();
        syncUndoRedoToCache();
      } finally {
        undoRedoInProgressRef.current = false;
      }
    };

    const handleRedo = async () => {
      const now = Date.now();
      if (undoRedoInProgressRef.current) return;
      if (now - lastUndoRedoRunRef.current.redo < UNDO_REDO_DEBOUNCE_MS) return;
      if (redoStackRef.current.length === 0) return;
      undoRedoInProgressRef.current = true;
      lastUndoRedoRunRef.current.redo = now;
      try {
        const action = redoStackRef.current.pop();
        if (!action) return;

        undoStackRef.current.push(action);
        if (action.payload && typeof action.payload === "object") {
          action.payload.wasUndone = false;
        }
        emitUndoRedoCapabilities();
        syncUndoRedoToCache();
        const rendition = renditionRef.current;
        if (action.type === "add-highlight") {
          const {
            cfiRange,
            content,
            color,
            page_number,
            chapter_number,
            highlightId,
          } = action.payload;
          const colorClass = getEpubHighlightColorClass(color || "#FFF59D");
          const highlightStyles = getEpubHighlightStyles(color || "#FFF59D");
          if (rendition && cfiRange) {
            try {
              rendition.annotations.add(
                "highlight",
                cfiRange,
                { id: highlightId || null },
                (e) =>
                  handleHighlightClickRef.current?.(
                    cfiRange,
                    action?.payload?.highlightId || highlightId || null,
                    content || "",
                    e,
                  ),
                colorClass,
                highlightStyles,
              );
            } catch (e) {}
          }
          try {
            const highlights = await createHighlight(
              {
                page_number,
                content,
                color: color || "#FFF59D",
                coordinates: { cfiRange },
                chapter_number,
              },
              { refresh: false },
            );
            const created = Array.isArray(highlights)
              ? highlights.find((h) => {
                  const c = h.coordinates;
                  if (!c) return false;
                  const cfi =
                    c.cfiRange ??
                    c.cfi_range ??
                    (Array.isArray(c)
                      ? (c[0]?.cfiRange ?? c[0]?.cfi_range)
                      : undefined);
                  return cfi === cfiRange;
                })
              : highlights;
            if (created?.id) {
              action.payload.highlightId = created.id;
              syncAnnotationIdAcrossStacks({
                type: "highlight",
                cfiRange,
                annotationId: created.id,
              });
            }
          } catch (e) {
            console.error("Redo: create highlight failed", e);
          }
        } else if (action.type === "add-underline") {
          const {
            cfiRange,
            content,
            page_number,
            chapter_number,
            underlineId,
          } = action.payload;
          if (rendition && cfiRange) {
            try {
              rendition.annotations.add(
                "underline",
                cfiRange,
                { id: underlineId || null },
                (e) =>
                  handleUnderlineClickRef.current?.(
                    cfiRange,
                    action?.payload?.underlineId || underlineId || null,
                    content || "",
                    e,
                  ),
                "epub-underline",
              );
            } catch (e) {}
          }
          try {
            const newUnderline = await createUnderline(
              {
                page_number,
                content,
                coordinates: { cfiRange },
                chapter_number,
              },
              { refresh: false },
            );
            if (newUnderline?.id) {
              action.payload.underlineId = newUnderline.id;
              syncAnnotationIdAcrossStacks({
                type: "underline",
                cfiRange,
                annotationId: newUnderline.id,
              });
            }
          } catch (e) {
            console.error("Redo: create underline failed", e);
          }
        } else if (action.type === "delete-highlight") {
          const { cfiRange, highlightId } = action.payload || {};
          if (rendition && cfiRange) {
            try {
              rendition.annotations.remove(cfiRange, "highlight");
            } catch (e) {}
          }
          if (highlightId) {
            try {
              removeHighlightById(highlightId);
            } catch (e) {
              console.error("Redo: delete highlight failed", e);
            }
          }
        } else if (action.type === "delete-underline") {
          const { cfiRange, underlineId } = action.payload || {};
          if (rendition && cfiRange) {
            try {
              rendition.annotations.remove(cfiRange, "underline");
            } catch (e) {}
          }
          if (underlineId) {
            try {
              deleteUnderline(underlineId);
            } catch (e) {
              console.error("Redo: delete underline failed", e);
            }
          }
        }
        emitUndoRedoCapabilities();
        syncUndoRedoToCache();
      } finally {
        undoRedoInProgressRef.current = false;
      }
    };

    window.addEventListener("reader:undo", handleUndo);
    window.addEventListener("reader:redo", handleRedo);
    return () => {
      window.removeEventListener("reader:undo", handleUndo);
      window.removeEventListener("reader:redo", handleRedo);
    };
  }, [
    getEpubHighlightColorClass,
    getEpubHighlightStyles,
    emitUndoRedoCapabilities,
    syncAnnotationIdAcrossStacks,
    syncUndoRedoToCache,
  ]);

  useEffect(() => {
    emitUndoRedoCapabilities();
  }, [emitUndoRedoCapabilities]);

  // NOTE: Sidebar open/close no longer triggers rendition resize for EPUB.
  // Desktop EPUB sidebars use overlay positioning (position:absolute), so
  // the viewer container width does not change. The ResizeObserver handles
  // any actual container dimension changes (e.g. window resize).

  // Track the current chunk's epub book for rendering
  const currentChunkBookRef = useRef(null);

  useEffect(() => {
    sessionExpiredNotifiedRef.current = false;
  }, [documentId, epubBook]);

  const showSessionExpiredOnce = useCallback(() => {
    if (sessionExpiredNotifiedRef.current) return;
    sessionExpiredNotifiedRef.current = true;
    showSessionExpiredNotification();
  }, []);

  const getEpubChapterForPage = useCallback(
    async (pageNumber) => {
      if (!metadata) return epubBook;

      if (!Array.isArray(metadata.chapters) || metadata.chapters.length === 0) {
        console.warn("No chapters metadata available");
        return epubBook;
      }

      const chapter = metadata.chapters.find((c) => {
        const startPage = Number(c.start_page) || 1;
        const endPage = Number(c.end_page) || 1;
        return pageNumber >= startPage && pageNumber <= endPage;
      });

      if (!chapter) {
        console.warn(`No chapter found for page ${pageNumber}`);
        return epubBook;
      }

      const chapterNumber = Number(chapter.chapter_number) || 1;

      if (loadedChunks[chapterNumber]) {
        return loadedChunks[chapterNumber];
      }

      try {
        const chapterDoc = await loadEpubChapter(chapterNumber);
        return chapterDoc || epubBook;
      } catch (err) {
        console.error(`Failed to load EPUB chapter ${chapterNumber}:`, err);
        showSessionExpiredOnce();
        return epubBook;
      }
    },
    [metadata, epubBook, loadedChunks, loadEpubChapter, showSessionExpiredOnce],
  );

  const getRelativeSectionIndex = useCallback(
    (pageNumber) => {
      if (!metadata || !Array.isArray(metadata.chapters)) return pageNumber;
      const chapter = metadata.chapters.find((c) => {
        const s = Number(c.start_page) || 1;
        const e = Number(c.end_page) || 1;
        return pageNumber + 1 >= s && pageNumber + 1 <= e;
      });
      if (!chapter) return pageNumber;
      const chapterStartPage = Number(chapter.start_page) || 1;
      return pageNumber + 1 - chapterStartPage;
    },
    [metadata],
  );

  const handleNextChunk = useCallback(() => {
    if (
      !metadata ||
      !Array.isArray(metadata.chapters) ||
      isRenderingRef.current
    )
      return;

    const currentChapterNumber = currentChunkIndexRef.current;
    let nextChapter = null;
    let minNextNumber = Infinity;

    for (const chapter of metadata.chapters) {
      const chapterNum = Number(chapter.chapter_number);
      if (chapterNum > currentChapterNumber && chapterNum < minNextNumber) {
        minNextNumber = chapterNum;
        nextChapter = chapter;
      }
    }

    if (nextChapter) {
      const nextChapterNumber = Number(nextChapter.chapter_number) || 1;
      const nextChapterStartPage = Number(nextChapter.start_page) || 1;

      // Let useEffect handle chunk index updates after loading
      // Update currentPageRef immediately to prevent race conditions during rapid clicking
      currentPageRef.current = nextChapterStartPage;
      skipCurrentPageSyncRef.current = false;
      setCurrentPageEnd(null);
      setCurrentPage(nextChapterStartPage);
    }
  }, [metadata, setCurrentPage, setCurrentPageEnd]);

  const handlePrevChunk = useCallback(() => {
    if (
      !metadata ||
      !Array.isArray(metadata.chapters) ||
      isRenderingRef.current
    )
      return;

    const currentChapterNumber = currentChunkIndexRef.current;
    let prevChapter = null;
    let maxPrevNumber = -Infinity;

    for (const chapter of metadata.chapters) {
      const chapterNum = Number(chapter.chapter_number);
      if (chapterNum < currentChapterNumber && chapterNum > maxPrevNumber) {
        maxPrevNumber = chapterNum;
        prevChapter = chapter;
      }
    }

    if (prevChapter) {
      const { startPage } = getChapterPageBounds(prevChapter);

      // Navigate to the START of the previous chapter. Navigating to endPage
      // is unreliable because seekRenditionToSyntheticPage positions scrollLeft
      // to a non-zero value, which gets corrupted when the loading overlay
      // hides (container resize → epub.js reflows → scrollLeft shifts).
      currentPageRef.current = startPage;
      skipCurrentPageSyncRef.current = false;
      setCurrentPageEnd(null);
      setCurrentPage(startPage);
    }
  }, [metadata, setCurrentPage, setCurrentPageEnd]);

  const getChapterForSyntheticPage = useCallback(
    (pageNumber) => {
      if (!Array.isArray(metadata?.chapters)) return null;
      const page = Number(pageNumber) || 1;
      return (
        metadata.chapters.find((chapter) => {
          const { startPage, endPage } = getChapterPageBounds(chapter);
          return page >= startPage && page <= endPage;
        }) || null
      );
    },
    [metadata],
  );

  // Navigate to next page within current chunk, or load next chunk if at end
  const handleNextPage = useCallback(async () => {
    if (isRenderingRef.current) return;

    const rendition = renditionRef.current;
    if (!rendition) return;

    // Clear any active selection in the EPUB iframe so highlights from a
    // previous Cmd+A / Ctrl+A don't bleed into the next visible column.
    try {
      const views = rendition.views?.() || [];
      views.forEach((v) => v?.window?.getSelection?.()?.removeAllRanges?.());
    } catch (_) {}

    try {
      const moved = await attemptPaginatedStep(rendition, "next");
      if (!moved) {
        atChapterEndRef.current = true;
        setAtChapterEnd(true);
        handleNextChunk();
      }
    } catch (error) {
      console.error("Error navigating to next page:", error);
    }
  }, [handleNextChunk]);

  // Navigate to previous page within current chunk, or load previous chunk if at start
  const handlePrevPage = useCallback(async () => {
    if (isRenderingRef.current) return;

    const rendition = renditionRef.current;
    if (!rendition) return;

    // Clear any active selection in the EPUB iframe so highlights from a
    // previous Cmd+A / Ctrl+A don't bleed into the next visible column.
    try {
      const views = rendition.views?.() || [];
      views.forEach((v) => v?.window?.getSelection?.()?.removeAllRanges?.());
    } catch (_) {}

    try {
      const moved = await attemptPaginatedStep(rendition, "prev");
      if (!moved) {
        atChapterStartRef.current = true;
        setAtChapterStart(true);
        handlePrevChunk();
      }
    } catch (error) {
      console.error("Error navigating to previous page:", error);
    }
  }, [handlePrevChunk]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    initialViewAppliedRef.current = false;
    setEpubReady(false);
  }, [epubBook, documentId]);

  // Apply initial_view.start_page from API/DRM once the rendition is ready.
  useEffect(() => {
    if (
      !epubBook ||
      !epubReady ||
      !initialViewStartPage ||
      initialViewAppliedRef.current
    ) {
      return;
    }

    const target = Number(initialViewStartPage);
    if (!Number.isInteger(target) || target <= 0) {
      clearInitialViewStartPage();
      return;
    }

    initialViewAppliedRef.current = true;
    currentPageRef.current = target;
    skipCurrentPageSyncRef.current = false;
    setCurrentPage(target);
    clearInitialViewStartPage();
    requestPageNavigation();
  }, [
    epubBook,
    epubReady,
    initialViewStartPage,
    clearInitialViewStartPage,
    setCurrentPage,
    requestPageNavigation,
  ]);

  // Scroll handler removed - using paginated horizontal navigation instead

  const handleKeyboardNavigation = useCallback(
    (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        const target = e.target;
        const isInput =
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable);

        if (isInput) return;
        if (
          document.activeElement &&
          (document.activeElement.tagName === "INPUT" ||
            document.activeElement.tagName === "TEXTAREA")
        ) {
          return;
        }

        // Spatial keys: in RTL, left advances reading order (epub.js examples).
        if (e.key === "ArrowRight") {
          e.preventDefault();
          if (isRtlReading) {
            handlePrevPage();
          } else {
            handleNextPage();
          }
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          if (isRtlReading) {
            handleNextPage();
          } else {
            handlePrevPage();
          }
        }
      }
    },
    [handleNextPage, handlePrevPage, isRtlReading],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyboardNavigation);
    return () => {
      window.removeEventListener("keydown", handleKeyboardNavigation);
    };
  }, [handleKeyboardNavigation]);

  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!target || !(target instanceof Element)) return false;
      if (target.isContentEditable) return true;
      const editableEl = target.closest(
        "input, textarea, select, [contenteditable='true']",
      );
      return !!editableEl;
    };

    const pickBestVisibleView = (views) => {
      const viewportRect = viewportRef.current?.getBoundingClientRect?.();
      if (!viewportRect) return views[0] || null;

      let bestView = null;
      let bestScore = -1;

      views.forEach((view) => {
        const frame = view?.element;
        if (!frame?.getBoundingClientRect) return;
        const rect = frame.getBoundingClientRect();
        const overlapW =
          Math.min(rect.right, viewportRect.right) -
          Math.max(rect.left, viewportRect.left);
        const overlapH =
          Math.min(rect.bottom, viewportRect.bottom) -
          Math.max(rect.top, viewportRect.top);
        const score = Math.max(0, overlapW) * Math.max(0, overlapH);
        if (score > bestScore) {
          bestScore = score;
          bestView = view;
        }
      });

      return bestView || views[0] || null;
    };

    const handleSelectAllWithinEpub = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "a") return;
      if (isEditableTarget(e.target)) return;

      const rendition = renditionRef.current;
      const views = rendition?.views?.() || [];
      const activeView = pickBestVisibleView(views);
      const doc = activeView?.document;
      const win = activeView?.window;
      if (!doc?.body || !win) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const selection = win.getSelection?.();
      if (!selection) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // epub.js paginated layout: The iframe is expanded to the FULL
      // multi-column width (all pages combined).  The visible "page" is
      // determined by `manager.container` in the PARENT document which uses
      // overflow:hidden + scrollLeft to show one column at a time.
      //
      // So inside the iframe, getClientRects() returns positions where ALL
      // text fits within [0 … totalWidth].  We need to figure out which
      // slice of that range corresponds to the currently visible column.
      //
      // Strategy:
      //   1. From the parent, get manager.container's visible window
      //      (scrollLeft → scrollLeft + offsetWidth)
      //   2. Get the iframe element's left offset within that container
      //   3. The visible slice of the iframe content =
      //      (container.scrollLeft − iframe.offsetLeft) to
      //      (container.scrollLeft − iframe.offsetLeft + container.offsetWidth)
      //
      // getClientRects() inside the iframe gives coordinates relative to the
      // iframe's own origin (not scrolled, since the iframe has no internal
      // scroll).  So we compare rects against [colStart, colEnd].

      const manager = rendition?.manager;
      const mgrContainer = manager?.container;
      // The wrapper div around the iframe created by epub.js
      const iframeWrapper = activeView?.element; // <div class="epub-view">

      let colStart = 0;
      let colEnd = win.innerWidth || doc.documentElement.clientWidth || 800;

      if (mgrContainer && iframeWrapper) {
        const scrollLeft = mgrContainer.scrollLeft || 0;
        const viewportWidth =
          mgrContainer.offsetWidth || mgrContainer.clientWidth || colEnd;
        // The iframe wrapper's left offset inside the scrolling container
        const wrapperLeft = iframeWrapper.offsetLeft || 0;
        colStart = scrollLeft - wrapperLeft;
        colEnd = colStart + viewportWidth;
      }

      const INSET = 2; // small tolerance for rounding

      // Walk text nodes and find those within the visible column
      const walker = doc.createTreeWalker(
        doc.body,
        NodeFilter.SHOW_TEXT,
        null,
        false,
      );
      let firstVisibleNode = null;
      let lastVisibleNode = null;

      let node;
      while ((node = walker.nextNode())) {
        if (!node.textContent || !node.textContent.trim()) continue;

        const tmpRange = doc.createRange();
        tmpRange.selectNodeContents(node);
        const rects = tmpRange.getClientRects();
        if (!rects || rects.length === 0) continue;

        // Check if ANY rect of this text node intersects [colStart, colEnd]
        let isVisible = false;
        for (let i = 0; i < rects.length; i++) {
          const r = rects[i];
          if (
            r.right > colStart + INSET &&
            r.left < colEnd - INSET &&
            r.width > 0
          ) {
            isVisible = true;
            break;
          }
        }
        if (!isVisible) continue;

        if (!firstVisibleNode) firstVisibleNode = node;
        lastVisibleNode = node;
      }

      if (firstVisibleNode && lastVisibleNode) {
        const range = doc.createRange();
        range.setStartBefore(firstVisibleNode);
        range.setEndAfter(lastVisibleNode);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // Fallback: select everything if we couldn't determine visibility
        // (e.g. fixed-layout EPUBs without columns).
        const range = doc.createRange();
        range.selectNodeContents(doc.body);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      win.focus?.();

      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener("keydown", handleSelectAllWithinEpub, true);
    return () => {
      document.removeEventListener("keydown", handleSelectAllWithinEpub, true);
    };
  }, []);

  const bookId = getBookId(documentName, documentType, epubBook);

  /**
   * Navigate to an EPUB CFI (highlight / underline / note).
   * Same-chapter: display(cfi). Cross-chapter: stash CFI + page nav so the
   * chapter-switch path displays it after load.
   */
  const navigateToAnnotationCfi = useCallback(
    async ({ cfiRange, chapterNumber = null, fallbackPage = null }) => {
      if (!cfiRange) {
        if (fallbackPage != null && !Number.isNaN(Number(fallbackPage))) {
          setCurrentPage(Number(fallbackPage));
        }
        return;
      }

      const currentChapter = Number(currentChunkIndexRef.current) || 0;
      let targetChapter =
        chapterNumber != null && !Number.isNaN(Number(chapterNumber))
          ? Number(chapterNumber)
          : null;

      if (targetChapter == null && fallbackPage != null) {
        const chapters = useReaderStore.getState().metadata?.chapters;
        if (Array.isArray(chapters)) {
          const page = Number(fallbackPage);
          const chapter = chapters.find((c) => {
            const startPage = Number(c.start_page) || 1;
            const endPage = Number(c.end_page) || 1;
            return page >= startPage && page <= endPage;
          });
          if (chapter) {
            targetChapter = Number(chapter.chapter_number) || currentChapter;
          }
        }
      }
      if (targetChapter == null) targetChapter = currentChapter;

      if (
        targetChapter === currentChapter &&
        renditionRef.current &&
        typeof renditionRef.current.display === "function" &&
        !isRenderingRef.current
      ) {
        pendingDisplayCfiRef.current = null;
        isSeekingRef.current = true;
        try {
          await renditionRef.current.display(cfiRange);
          await new Promise((resolve) => requestAnimationFrame(resolve));
        } catch (err) {
          console.warn(
            "[EPUB] display(cfi) failed; falling back to page:",
            err,
          );
          isSeekingRef.current = false;
          if (fallbackPage != null && !Number.isNaN(Number(fallbackPage))) {
            setCurrentPage(Number(fallbackPage));
          }
          return;
        } finally {
          isSeekingRef.current = false;
        }
        if (typeof renditionRef.current.reportLocation === "function") {
          renditionRef.current.reportLocation();
        }
        return;
      }

      pendingDisplayCfiRef.current = cfiRange;
      if (fallbackPage != null && !Number.isNaN(Number(fallbackPage))) {
        if (Number(currentPageRef.current) === Number(fallbackPage)) {
          requestPageNavigation();
        } else {
          setCurrentPage(Number(fallbackPage));
        }
      } else {
        requestPageNavigation();
      }
    },
    [requestPageNavigation, setCurrentPage],
  );

  const getCurrentLocationCfi = useCallback(() => {
    const rendition = renditionRef.current;
    if (!rendition) return null;
    try {
      const location =
        typeof rendition.currentLocation === "function"
          ? rendition.currentLocation()
          : rendition.location;
      return getLocationCfi(location);
    } catch {
      return null;
    }
  }, []);

  // Keep refs in sync with global toolbar toggles
  useEffect(() => {
    const handleOpenNote = async (ev) => {
      try {
        const note = ev?.detail?.note;
        if (!note || !bookId) return;

        const cfiRange = getHighlightCfiRange(note);
        const chapterNumber =
          note.chapter_number != null ? Number(note.chapter_number) : null;
        const fallbackPage =
          note.page_number != null ? Number(note.page_number) : null;

        if (cfiRange) {
          await navigateToAnnotationCfi({
            cfiRange,
            chapterNumber,
            fallbackPage,
          });
        } else if (fallbackPage != null && !Number.isNaN(fallbackPage)) {
          // Legacy notes without CFI — invertible synthetic seek.
          setCurrentPage(fallbackPage);
        }

        const allNotes = Array.isArray(notesFromStore) ? notesFromStore : [];
        const openedPage = Number(note.page_number);
        const sectionNotes = allNotes.filter(
          (n) => Number(n.page_number) === openedPage,
        );
        setNotes(sectionNotes);
        const idx = sectionNotes.findIndex(
          (n) => n && String(n.id) === String(note.id),
        );
        setCurrentNoteIndex(idx !== -1 ? idx : 0);
        const normalizedNote = {
          ...note,
          selectedText: note.selected_text || note.selectedText || "",
        };
        // open note overlay
        setEditingNote(normalizedNote);
        setNoteText(note?.content || "");
        setNotePosition({ x: 50, y: 50 });
        setExpandedSelectedText(false);
        // Hide any visible context menu to avoid overlapping popups
        setContextMenuVisible(false);
        setShowNoteInput(true);
        setTimeout(() => {
          const ta = document.querySelector(".note-input-textarea");
          if (ta) {
            ta.focus();
            ta.setSelectionRange(ta.value.length, ta.value.length);
          }
        }, 50);
      } catch (err) {
        console.error("Error opening note in EPUB viewer:", err);
      }
    };

    window.addEventListener("reader:open-note", handleOpenNote);
    return () => window.removeEventListener("reader:open-note", handleOpenNote);
  }, [
    bookId,
    getHighlightCfiRange,
    navigateToAnnotationCfi,
    notesFromStore,
    setCurrentPage,
    setNotesEnabled,
  ]);

  // Notebook highlight/underline jump: navigate by CFI (exact locus), not synthetic page.
  useEffect(() => {
    const handleFocusAnnotation = async (ev) => {
      try {
        const detail = ev?.detail || {};
        const type = detail.type;
        if (type !== "highlight" && type !== "underline") return;

        let cfiRange = detail.cfiRange || null;
        let chapterNumber =
          detail.chapter_number != null ? Number(detail.chapter_number) : null;
        const fallbackPage =
          detail.page != null ? Number(detail.page) : null;

        // Resolve from store when event only has id (or CFI missing).
        if (!cfiRange && detail.id != null) {
          const store = useReaderStore.getState();
          const list =
            type === "highlight"
              ? store.annotations?.highlights?.data
              : store.annotations?.underlines?.data;
          const found = Array.isArray(list)
            ? list.find((a) => a && String(a.id) === String(detail.id))
            : null;
          if (found) {
            cfiRange = getHighlightCfiRange(found);
            if (chapterNumber == null && found.chapter_number != null) {
              chapterNumber = Number(found.chapter_number);
            }
          }
        }

        await navigateToAnnotationCfi({
          cfiRange,
          chapterNumber,
          fallbackPage,
        });
      } catch (err) {
        console.error("Error focusing EPUB annotation:", err);
      }
    };

    window.addEventListener("reader:focus-annotation", handleFocusAnnotation);
    return () =>
      window.removeEventListener(
        "reader:focus-annotation",
        handleFocusAnnotation,
      );
  }, [getHighlightCfiRange, navigateToAnnotationCfi]);

  // Ensure note input and context menu are mutually exclusive to avoid overlap.
  useEffect(() => {
    if (contextMenuVisible && showNoteInput) {
      // If context menu opened while note editor is visible, close the note editor.
      setShowNoteInput(false);
    }
  }, [contextMenuVisible, showNoteInput]);

  useEffect(() => {
    if (showNoteInput && contextMenuVisible) {
      // If note editor opened while context menu is visible, hide the context menu.
      setContextMenuVisible(false);
    }
  }, [showNoteInput, contextMenuVisible]);

  useEffect(() => {
    const handleFitToWidth = () => {
      setReaderViewMode("fit-to-width");
    };

    const handleFitToPage = () => {
      setReaderViewMode("fit-to-page");
    };

    window.addEventListener("reader:fit-to-width", handleFitToWidth);
    window.addEventListener("reader:fit-to-page", handleFitToPage);

    return () => {
      window.removeEventListener("reader:fit-to-width", handleFitToWidth);
      window.removeEventListener("reader:fit-to-page", handleFitToPage);
    };
  }, [setReaderViewMode]);

  // Use ResizeObserver to automatically resize epubjs rendition whenever the container size changes
  useEffect(() => {
    if (!renditionRef.current || !containerRef.current) return;

    let resizeTimer = null;
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries && entries[0];
      const observedWidth = Math.round(
        entry?.contentRect?.width || containerRef.current?.clientWidth || 0,
      );
      const observedHeight = Math.round(
        entry?.contentRect?.height || containerRef.current?.clientHeight || 0,
      );
      if (observedWidth <= 0 || observedHeight <= 0) return;

      const prevWidth = lastObservedContainerWidthRef.current;
      // Ignore height-only/content-driven mutations to avoid resize-render loops.
      if (prevWidth > 0 && Math.abs(observedWidth - prevWidth) < 2) return;
      lastObservedContainerWidthRef.current = observedWidth;

      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (renditionRef.current) {
          try {
            // Pass actual dimensions for paginated layout
            renditionRef.current.resize(observedWidth, observedHeight);
          } catch (e) {}
        }
      }, 200);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(resizeTimer);
      resizeObserver.disconnect();
    };
  }, []);

  // Force resize when view mode changes to recalculate paginated layout.
  // NOTE: Sidebar states removed — EPUB sidebars are now overlays
  // (position:absolute) and don't change the container width.
  useEffect(() => {
    if (!renditionRef.current || !containerRef.current) return;

    // Add a small delay to allow the CSS transition to complete
    const resizeTimer = setTimeout(() => {
      const container = containerRef.current;
      if (!container || !renditionRef.current) return;

      try {
        const width = container.clientWidth || window.innerWidth;
        const height = container.clientHeight || window.innerHeight - 100;
        if (width > 0 && height > 0) {
          renditionRef.current.resize(width, height);
          lastObservedContainerWidthRef.current = width;
        }
      } catch (e) {
        console.warn("Error resizing EPUB after layout change:", e);
      }
    }, 350); // Wait for CSS transition (300ms) + buffer

    return () => clearTimeout(resizeTimer);
  }, [readerViewMode]);

  // Reset direction tracking when the open book changes.
  useEffect(() => {
    lastAppliedReadingDirectionRef.current = null;
  }, [epubBook, documentId]);

  // Apply reading direction changes from metadata / Display menu.
  // Must sync Layout.settings.direction (epub.js gap) or only page numbers flip.
  useEffect(() => {
    const rendition = renditionRef.current;
    const book = currentChunkBookRef.current || epubBook;
    if (!rendition || !book || !epubReady) return;

    const layoutDir = getEpubReadingDirection(rendition);
    const alreadyApplied = lastAppliedReadingDirectionRef.current;

    // First observation after renderTo: direction was already set at init.
    if (alreadyApplied == null) {
      lastAppliedReadingDirectionRef.current = layoutDir;
      void applyEpubReadingDirection(book, readingDirection, null);
      if (layoutDir === readingDirection) return;
    } else if (
      alreadyApplied === readingDirection &&
      layoutDir === readingDirection
    ) {
      return;
    }

    let cancelled = false;

    const switchDirection = async () => {
      const preservedPage = Number(currentPageRef.current) || 1;
      const preservedPageEnd =
        useReaderStore.getState().currentPageEnd ?? null;
      const chapter = getChapterByNumber(
        metadata,
        currentChunkIndexRef.current,
      );
      const locationBefore =
        typeof rendition.currentLocation === "function"
          ? rendition.currentLocation()
          : rendition.location;
      const preservedCfi = locationBefore?.start?.cfi || null;

      // Suppress relocated page remapping while direction/layout resettles.
      isSeekingRef.current = true;
      try {
        await applyEpubReadingDirection(book, readingDirection, rendition, {
          force: true,
          cfi: preservedCfi,
        });
        if (cancelled) return;
        lastAppliedReadingDirectionRef.current = readingDirection;

        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (cancelled) return;

        // Restore single/double spread after clear+display (honors DRM cover isolation).
        const livePage =
          Number(useReaderStore.getState().currentPage) || preservedPage;
        const spreadValue = resolveEpubSpreadValue(
          allowDoublePage,
          pageViewMode,
          drmPolicy,
          livePage,
        );
        const minSpreadWidth = spreadValue === "auto" ? 1024 : 800;
        try {
          rendition.spread(spreadValue, minSpreadWidth);
        } catch (_) {}

        const container = containerRef.current;
        if (container && typeof rendition.resize === "function") {
          const width = container.clientWidth || window.innerWidth;
          const height = container.clientHeight || window.innerHeight - 100;
          if (width > 0 && height > 0) {
            rendition.resize(width, height);
          }
        }

        if (preservedCfi && typeof rendition.display === "function") {
          await rendition.display(preservedCfi);
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (cancelled) return;

        // Keep the user's place label; same CFI = same content.
        currentPageRef.current = preservedPage;
        skipCurrentPageSyncRef.current = true;
        setCurrentPageWithEnd(preservedPage, preservedPageEnd);

        if (chapter) {
          const { startPage, endPage } = getChapterPageBounds(chapter);
          const pageEnd = Number(preservedPageEnd) || preservedPage;
          const atStart = preservedPage <= startPage;
          const atEnd = pageEnd >= endPage;
          setAtChapterStart(atStart);
          atChapterStartRef.current = atStart;
          setAtChapterEnd(atEnd);
          atChapterEndRef.current = atEnd;
        }
      } catch (err) {
        console.warn("Failed to switch EPUB reading direction:", err);
      } finally {
        requestAnimationFrame(() => {
          isSeekingRef.current = false;
        });
      }
    };

    switchDirection();
    return () => {
      cancelled = true;
    };
  }, [
    readingDirection,
    epubBook,
    epubReady,
    metadata,
    allowDoublePage,
    pageViewMode,
    drmPolicy,
    setCurrentPageWithEnd,
  ]);

  // Handle pageViewMode / DRM cover isolation changes - update epub.js spread setting
  useEffect(() => {
    if (!renditionRef.current) return;

    const rendition = renditionRef.current;
    const spreadValue = epubSpreadValue;
    const minSpreadWidth = spreadValue === "auto" ? 1024 : 800;

    try {
      // Update the spread setting with minSpreadWidth
      rendition.spread(spreadValue, minSpreadWidth);

      // Force resize to apply the new layout
      const container = containerRef.current;
      if (container) {
        const width = container.clientWidth || window.innerWidth;
        const height = container.clientHeight || window.innerHeight - 100;
        if (width > 0 && height > 0) {
          setTimeout(() => {
            if (renditionRef.current) {
              renditionRef.current.resize(width, height);
            }
          }, 100);
        }
      }
    } catch (e) {
      console.warn("Error updating EPUB spread mode:", e);
    }
  }, [epubSpreadValue]);

  useEffect(() => {
    highlightEnabledRef.current = !!highlightEnabled;
  }, [highlightEnabled]);

  useEffect(() => {
    underlineEnabledRef.current = !!underlineEnabled;
  }, [underlineEnabled]);

  useEffect(() => {
    isFixedLayoutRef.current = isFixedLayout;
  }, [isFixedLayout]);

  // Handler for when a user clicks on an existing highlight annotation
  const handleHighlightClick = useCallback(
    (cfiRange, highlightId, highlightContent, clickEvent) => {
      if (!renditionRef.current) return;
      try {
        const bounds = getAnnotationOverlayRect(cfiRange, clickEvent);
        if (!bounds) return;

        setSelectedUnderlineOverlay(null);
        setSelectedHighlightOverlay({
          cfiRange,
          highlightId,
          content: highlightContent || "",
          bounds,
        });

        // Show the same selection toolbar used for text selection
        suppressAnnotationMenuCloseUntilRef.current = Date.now() + 500;
        setContextMenuPosition({
          x: bounds.left + bounds.width / 2 - 140,
          y: bounds.top + bounds.height,
        });
        setSelectionRect({
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
          right: bounds.left + bounds.width,
          bottom: bounds.top + bounds.height,
        });
        setSelectedText(highlightContent || "");
        setContextMenuVisible(true);
      } catch (err) {
        console.error("Error handling highlight click:", err);
      }
    },
    [getAnnotationOverlayRect],
  );
  handleHighlightClickRef.current = handleHighlightClick;

  const handleUnderlineClick = useCallback(
    (cfiRange, underlineId, underlineContent, clickEvent) => {
      if (!renditionRef.current) return;
      try {
        const bounds = getAnnotationOverlayRect(cfiRange, clickEvent);
        if (!bounds) return;

        setSelectedHighlightOverlay(null);
        setSelectedUnderlineOverlay({
          cfiRange,
          underlineId,
          content: underlineContent || "",
          bounds,
        });

        suppressAnnotationMenuCloseUntilRef.current = Date.now() + 500;
        setContextMenuPosition({
          x: bounds.left + bounds.width / 2 - 140,
          y: bounds.top + bounds.height,
        });
        setSelectionRect({
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
          right: bounds.left + bounds.width,
          bottom: bounds.top + bounds.height,
        });
        setSelectedText(underlineContent || "");
        setContextMenuVisible(true);
      } catch (err) {
        console.error("Error handling underline click:", err);
      }
    },
    [getAnnotationOverlayRect],
  );
  handleUnderlineClickRef.current = handleUnderlineClick;

  const handleSelectedHighlightDelete = useCallback(async () => {
    if (!selectedHighlightOverlay || undoRedoInProgressRef.current) return;

    const overlay = selectedHighlightOverlay;
    setSelectedHighlightOverlay(null);

    const pageNum = currentPageRef.current ?? 1;
    const highlightFromStore = (highlightsFromStore || []).find((h) => {
      if (overlay.highlightId && h.id === overlay.highlightId) {
        return true;
      }
      return getHighlightCfiRange(h) === overlay.cfiRange;
    });

    const highlightDeleteId = highlightFromStore?.id || overlay.highlightId;
    let deletedFromApi = false;

    try {
      if (renditionRef.current) {
        try {
          renditionRef.current.annotations.remove(
            overlay.cfiRange,
            "highlight",
          );
        } catch (err) {
          console.warn("Error removing annotation visually:", err);
        }
      }

      if (highlightDeleteId) {
        removeHighlightById(highlightDeleteId);
        deletedFromApi = true;
      }

      if (deletedFromApi) {
        pushAction(pageNum, {
          type: "delete-highlight",
          payload: {
            highlightId: highlightDeleteId,
            cfiRange: overlay.cfiRange,
            content: highlightFromStore?.content || overlay.content,
            color: highlightFromStore?.color || "#FFF59D",
            page_number: pageNum,
            chapter_number:
              highlightFromStore?.chapter_number ??
              currentChunkIndexRef.current ??
              1,
          },
        });
      }
    } catch (err) {
      console.error("Error handling selected highlight delete:", err);
    }
  }, [
    getHighlightCfiRange,
    highlightsFromStore,
    pushAction,
    selectedHighlightOverlay,
  ]);

  const handleSelectedUnderlineDelete = useCallback(async () => {
    if (!selectedUnderlineOverlay || undoRedoInProgressRef.current) return;

    const overlay = selectedUnderlineOverlay;
    setSelectedUnderlineOverlay(null);

    const pageNum = currentPageRef.current ?? 1;
    const underlineFromStore = (underlinesFromStore || []).find((u) => {
      if (overlay.underlineId && u.id === overlay.underlineId) {
        return true;
      }
      const uc = u.coordinates;
      const ucfi = uc?.cfiRange ?? uc?.cfi_range;
      return ucfi && String(ucfi) === String(overlay.cfiRange);
    });

    const underlineDeleteId = underlineFromStore?.id || overlay.underlineId;
    let deletedFromApi = false;

    try {
      if (renditionRef.current) {
        try {
          renditionRef.current.annotations.remove(
            overlay.cfiRange,
            "underline",
          );
        } catch (err) {
          console.warn("Error removing annotation visually:", err);
        }
      }

      if (underlineDeleteId) {
        deleteUnderline(underlineDeleteId);
        deletedFromApi = true;
      }

      if (deletedFromApi) {
        pushAction(pageNum, {
          type: "delete-underline",
          payload: {
            underlineId: underlineDeleteId,
            cfiRange: overlay.cfiRange,
            content: underlineFromStore?.content || overlay.content,
            page_number: pageNum,
            chapter_number:
              underlineFromStore?.chapter_number ??
              currentChunkIndexRef.current ??
              1,
          },
        });
      }
    } catch (err) {
      console.error("Error handling selected underline delete:", err);
    }
  }, [pushAction, selectedUnderlineOverlay, underlinesFromStore]);

  useEffect(() => {
    if (!contextMenuVisible) return;
    setSelectedHighlightOverlay(null);
    setSelectedUnderlineOverlay(null);
  }, [contextMenuVisible]);

  useEffect(() => {
    const hasHighlight = !!selectedHighlightOverlay?.cfiRange;
    const hasUnderline = !!selectedUnderlineOverlay?.cfiRange;
    if (!hasHighlight && !hasUnderline) return;

    const syncOverlayBounds = () => {
      if (hasHighlight) {
        setSelectedHighlightOverlay((prev) => {
          if (!prev?.cfiRange) return prev;
          const nextBounds = getAnnotationOverlayRect(prev.cfiRange, null);
          if (!nextBounds) return prev;
          return { ...prev, bounds: nextBounds };
        });
      }

      if (hasUnderline) {
        setSelectedUnderlineOverlay((prev) => {
          if (!prev?.cfiRange) return prev;
          const nextBounds = getAnnotationOverlayRect(prev.cfiRange, null);
          if (!nextBounds) return prev;
          return { ...prev, bounds: nextBounds };
        });
      }
    };

    syncOverlayBounds();

    let rafId = null;
    const scheduleSync = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        syncOverlayBounds();
      });
    };

    const viewportEl = viewportRef.current;
    const containerEl = containerRef.current;

    window.addEventListener("resize", scheduleSync, true);
    window.addEventListener("scroll", scheduleSync, true);
    viewportEl?.addEventListener("scroll", scheduleSync, true);
    containerEl?.addEventListener("scroll", scheduleSync, true);

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", scheduleSync, true);
      window.removeEventListener("scroll", scheduleSync, true);
      viewportEl?.removeEventListener("scroll", scheduleSync, true);
      containerEl?.removeEventListener("scroll", scheduleSync, true);
    };
  }, [
    getAnnotationOverlayRect,
    selectedHighlightOverlay?.cfiRange,
    selectedUnderlineOverlay?.cfiRange,
  ]);

  useEffect(() => {
    if (!selectedHighlightOverlay && !selectedUnderlineOverlay) return;

    const handleDocumentPointerDown = (event) => {
      if (Date.now() < suppressAnnotationMenuCloseUntilRef.current) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        setSelectedHighlightOverlay(null);
        setSelectedUnderlineOverlay(null);
        return;
      }

      // Keep annotation chrome while interacting with the selection toolbar
      // (portaled to body) or its language dropdown panel.
      if (
        target.closest(".epub-annotation-selection-overlay") ||
        target.closest(".epub-annotation-selection-delete") ||
        target.closest(".context-menu") ||
        target.closest(".language-dropdown-panel")
      ) {
        return;
      }

      setSelectedHighlightOverlay(null);
      setSelectedUnderlineOverlay(null);
      setContextMenuVisible(false);
      setSelectionRect(null);
    };

    document.addEventListener("mousedown", handleDocumentPointerDown, true);
    document.addEventListener("touchstart", handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener(
        "mousedown",
        handleDocumentPointerDown,
        true,
      );
      document.removeEventListener(
        "touchstart",
        handleDocumentPointerDown,
        true,
      );
    };
  }, [selectedHighlightOverlay, selectedUnderlineOverlay]);

  useEffect(() => {
    if (!renditionRef.current || !Array.isArray(highlightsFromStore)) return;
    if (isRenderingRef.current) return;
    if (undoRedoInProgressRef.current) return; // Skip rendering during undo/redo

    const restoreHighlights = () => {
      const rendition = renditionRef.current;
      if (!rendition) return;

      const views = rendition.views();
      if (!views?._views?.length) {
        return;
      }

      const chunkNum = Number(currentChunkIndex);

      // Filter highlights for the current chapter/chunk.
      // In continuous scroll mode, the entire chapter is loaded so we
      // should register ALL highlights for this chapter — epubjs will
      // automatically attach each highlight to the correct spine item
      // via its internal inject hook.
      const chapterHighlights = getHighlightsForChapter(chunkNum);

      try {
        // epubjs stores annotations as a hash of encodeURI(cfiRange + type)
        // Only remove USER highlight annotations — skip search highlights
        const annotations = rendition.annotations._annotations || {};
        Object.values(annotations).forEach((ann) => {
          if (
            ann?.cfiRange &&
            ann?.type === "highlight" &&
            !isSearchHighlightAnnotation(ann)
          ) {
            try {
              renditionRef.current.annotations.remove(
                ann.cfiRange,
                "highlight",
              );
            } catch {}
          }
        });
      } catch {}

      if (chapterHighlights.length === 0) return;

      const viewList = views?._views || [];

      viewList.forEach((view) => {
        const mergedHighlights = getMergedHighlightsForView(
          view,
          chapterHighlights,
        );

        mergedHighlights.forEach(({ range, highlight }) => {
          try {
            const cfiRange = view.contents?.cfiFromRange?.(range);
            if (!cfiRange) return;

            const hlId = highlight.id;
            const hlContent = highlight.content || "";
            const hlColor = highlight.color || "#FFF59D";
            const colorClass = getEpubHighlightColorClass(hlColor);
            const highlightStyles = getEpubHighlightStyles(hlColor);
            renditionRef.current.annotations.add(
              "highlight",
              cfiRange,
              { id: hlId },
              (e) => handleHighlightClick(cfiRange, hlId, hlContent, e),
              colorClass,
              highlightStyles,
            );
          } catch {
            // Skip invalid highlights silently
          }
        });
      });
    };

    const timer = setTimeout(restoreHighlights, 200);
    return () => clearTimeout(timer);
  }, [
    highlightsFromStore,
    currentChunkIndex,
    handleHighlightClick,
    getEpubHighlightColorClass,
    getEpubHighlightStyles,
    getHighlightsForChapter,
    getSimpleCfiBounds,
    getMergedHighlightsForView,
    isRendering,
  ]);

  useEffect(() => {
    if (!renditionRef.current || !Array.isArray(underlinesFromStore)) return;
    if (isRenderingRef.current) return;
    if (undoRedoInProgressRef.current) return; // Skip rendering during undo/redo

    const restoreUnderlines = () => {
      const rendition = renditionRef.current;
      if (!rendition) return;

      const views = rendition.views();
      if (!views?._views?.length) {
        return;
      }

      const chunkNum = Number(currentChunkIndex);

      // Filter underlines for the current chapter/chunk
      const chapterUnderlines = underlinesFromStore.filter((u) => {
        const uChapter = Number(u.chapter_number);
        if (u.chapter_number !== undefined && !Number.isNaN(uChapter)) {
          return uChapter === chunkNum;
        }
        const uPage = Number(u.page_number);
        return (
          !Number.isNaN(uPage) && isPageWithinChapterRange(uPage, chunkNum)
        );
      });

      try {
        const annotations = rendition.annotations._annotations || {};
        Object.values(annotations).forEach((ann) => {
          if (ann?.cfiRange && ann?.type === "underline") {
            try {
              renditionRef.current.annotations.remove(
                ann.cfiRange,
                "underline",
              );
            } catch {}
          }
        });
      } catch {}

      if (chapterUnderlines.length === 0) return;

      const existingCfis = new Set();

      chapterUnderlines.forEach((underline) => {
        const cfiRange = underline.coordinates?.cfiRange;
        if (!cfiRange || !renditionRef.current) return;

        try {
          // Remove any existing annotation for this CFI first
          if (existingCfis.has(cfiRange)) {
            try {
              renditionRef.current.annotations.remove(cfiRange, "underline");
            } catch {}
          }

          const ulId = underline.id;
          const ulContent = underline.content || "";
          // Add underline annotation with click handler
          renditionRef.current.annotations.add(
            "underline",
            cfiRange,
            { id: ulId },
            (e) => handleUnderlineClick(cfiRange, ulId, ulContent, e),
            "epub-underline",
          );
          existingCfis.add(cfiRange);
        } catch {
          // Skip invalid underlines silently
        }
      });
    };

    const timer = setTimeout(restoreUnderlines, 200);
    return () => clearTimeout(timer);
  }, [
    underlinesFromStore,
    currentChunkIndex,
    handleUnderlineClick,
    isPageWithinChapterRange,
    isRendering,
  ]);

  useEffect(() => {
    notesEnabledRef.current = !!notesEnabled;
  }, [notesEnabled]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  // Unified effect: apply/clear search highlights.
  // Runs on a 250ms timer so it fires AFTER restoreHighlights (200ms),
  // eliminating the race where restoreHighlights would wipe search marks.
  useEffect(() => {
    if (!renditionRef.current) return;

    // When search results are empty or cleared, remove any active ones
    if (!Array.isArray(searchResults) || searchResults.length === 0) {
      clearAllEpubSearchHighlights(renditionRef.current);
      return;
    }

    // Apply search highlights after a delay that exceeds the
    // restoreHighlights timer (200ms) to guarantee ordering.
    const timer = setTimeout(() => {
      const rendition = renditionRef.current;
      if (!rendition) return;

      // Clear stale search highlights first (from a previous chapter),
      // then re-apply for the current chunk.
      clearAllEpubSearchHighlights(rendition);
      applyEpubSearchHighlights(rendition, searchResults, currentChunkIndex);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchResults, currentChunkIndex]);

  // Clear search highlights when search sidebar closes AND search results are
  // cleared from the store.  We check both conditions so that mobile's
  // hideSearchSidebar (which only hides the panel but keeps searchResults)
  // does NOT wipe the highlights.
  useEffect(() => {
    if (
      !isSearchSidebarOpen &&
      (!Array.isArray(searchResults) || searchResults.length === 0)
    ) {
      if (renditionRef.current) {
        clearAllEpubSearchHighlights(renditionRef.current);
      }
    }
  }, [isSearchSidebarOpen, searchResults]);

  // Focus textarea when note input becomes visible
  useEffect(() => {
    if (showNoteInput) {
      // Use a small delay to ensure the DOM has updated
      const timer = setTimeout(() => {
        const textarea = document.querySelector(".note-input-textarea");
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(
            textarea.value.length,
            textarea.value.length,
          );
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showNoteInput]);

  // Load notes for current section
  useEffect(() => {
    if (!bookId) {
      setNotes([]);
      setCurrentNoteIndex(0);
      return;
    }
    const allNotes = Array.isArray(notesFromStore) ? notesFromStore : [];
    const pageNum = Number(currentPage);
    const sectionNum = Number(currentSection);
    // While the editor is open, keep the opened note's page group so arrow
    // navigation (and CFI seeks) don't drop notes from the carousel.
    const editingPage =
      showNoteInput && editingNote?.page_number != null
        ? Number(editingNote.page_number)
        : null;
    const sectionNotes = allNotes.filter((note) => {
      const notePage = Number(note.page_number);
      if (Number.isNaN(notePage)) return false;
      return (
        notePage === pageNum ||
        notePage === sectionNum ||
        (editingPage != null &&
          !Number.isNaN(editingPage) &&
          notePage === editingPage)
      );
    });
    setNotes(sectionNotes);

    // Reset currentNoteIndex if it's out of bounds
    if (currentNoteIndex >= sectionNotes.length) {
      setCurrentNoteIndex(0);
    }

    // Close overlay if editing note was removed from the store (not merely
    // off the current synthetic page during a same-group CFI seek).
    if (editingNote && !editingNote.isNew) {
      const existsInStore = allNotes.some(
        (n) => n && String(n.id) === String(editingNote.id),
      );
      if (!existsInStore) {
        closeEditingNote();
      }
    }
  }, [
    bookId,
    currentSection,
    currentPage,
    noteListVersion,
    currentNoteIndex,
    editingNote,
    notesFromStore,
    showNoteInput,
  ]);

  // Delay first save so we don't overwrite with page 1 before restore/initial view runs
  const saveEnabledRef = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      saveEnabledRef.current = true;
    }, 2500);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!documentId || !saveEnabledRef.current) return;
    const timer = setTimeout(() => {
      localStorage.setItem(
        `igp_reader_state_${documentId}`,
        JSON.stringify({
          currentPage,
          scale,
          lastUpdated: Date.now(),
          documentId,
        }),
      );
      devLog(`💾 Saved EPUB reader state: page ${currentPage}`);
    }, 1000); // Debounce: save after 1 second of no changes

    return () => clearTimeout(timer);
  }, [currentPage, scale, documentId]);

  // ---------- RENDITION UTILITIES & HANDLERS ----------

  const patchRendition = useCallback((rendition) => {
    if (!rendition) return;

    // Handle highlight errors gracefully
    try {
      if (rendition.annotations?._annotations) {
        const originalAdd = rendition.annotations.add.bind(
          rendition.annotations,
        );
        rendition.annotations.add = function (
          type,
          cfiRange,
          data,
          cb,
          className,
          styles,
        ) {
          try {
            return originalAdd(type, cfiRange, data, cb, className, styles);
          } catch {
            return null;
          }
        };
      }

      rendition.hooks.content.register((contents) => {
        if (contents.pane?.render) {
          const originalPaneRender = contents.pane.render.bind(contents.pane);
          contents.pane.render = function () {
            try {
              return originalPaneRender();
            } catch (err) {
              if (err.message?.includes("getClientRects")) return;
              throw err;
            }
          };
        }
      });

      // Inject CSS to ensure images fit properly in paginated mode
      rendition.hooks.content.register((contents) => {
        try {
          const doc = contents.document;
          if (!doc || doc.__igpImageStylesPatched) return;
          doc.__igpImageStylesPatched = true;

          const style = doc.createElement("style");
          style.textContent = `
            /* Ensure images fit within page columns in paginated mode */
            img, svg, figure, picture, video {
              max-width: 100% !important;
              height: auto !important;
              box-sizing: border-box !important;
              display: block !important;
              margin-left: auto !important;
              margin-right: auto !important;
            }
            /* Prevent images from breaking across columns */
            img, svg, figure, picture {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
            /* Handle SVG specifically */
            svg {
              max-height: 80vh !important;
              width: auto !important;
            }
            /* Ensure figures don't overflow */
            figure {
              overflow: hidden !important;
              width: 100% !important;
            }
          `;
          doc.head.appendChild(style);
        } catch (e) {
          console.warn("Could not inject image styles:", e);
        }
      });
    } catch (err) {
      console.warn("Rendition patching failed:", err);
    }

    // Swallow epub.js "No Section Found" errors from internal _display
    try {
      if (rendition._display && !rendition.__igpDisplayPatched) {
        rendition.__igpDisplayPatched = true;
        const originalDisplayInternal = rendition._display.bind(rendition);
        rendition._display = function patchedDisplay(target, ...args) {
          const handleError = (err) => {
            const message = err?.message || String(err || "");
            if (message && message.includes("No Section Found")) {
              console.warn(
                'Suppressed epub.js "No Section Found" for target:',
                target,
              );
              return null;
            }
            throw err;
          };

          try {
            const result = originalDisplayInternal(target, ...args);
            if (!result || typeof result.then !== "function") {
              return result;
            }
            return result.catch(handleError);
          } catch (err) {
            return handleError(err);
          }
        };
      }
    } catch (err) {
      console.warn("Rendition display patching failed:", err);
    }

    // Fresh rendition — allow theme apply even if key matches previous book.
    lastEpubThemeApplyKeyRef.current = "";
    applyCurrentEpubThemeRef.current?.(rendition, { force: true });
  }, []);

  // Keep typography refs in sync for pinch + content hooks
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    themeRef.current = theme || "dark";
  }, [theme]);

  useEffect(() => {
    epubFontFamilyRef.current = epubFontFamily || "original";
  }, [epubFontFamily]);

  useEffect(() => {
    epubLineHeightRef.current =
      typeof epubLineHeight === "number"
        ? epubLineHeight
        : EPUB_LINE_HEIGHT_DEFAULT;
  }, [epubLineHeight]);

  /**
   * Single entry for EPUB content theme + typography. Local CSS only (no API).
   * Dedupes identical applies so scale/theme/font effects don't double-work.
   */
  const applyCurrentEpubTheme = useCallback((rendition, options = {}) => {
    const target = rendition || renditionRef.current;
    if (!target?.themes?.default) return;

    const {
      scale: scaleOverride,
      themeId: themeOverride,
      fontId: fontOverride,
      lineHeight: lineHeightOverride,
      isFixedLayout: fixedOverride,
      force = false,
    } = options;

    const nextTheme = themeOverride ?? themeRef.current ?? "dark";
    const nextFont = fontOverride ?? epubFontFamilyRef.current ?? "original";
    const nextLineHeight =
      typeof lineHeightOverride === "number"
        ? lineHeightOverride
        : epubLineHeightRef.current;
    const nextFixed =
      typeof fixedOverride === "boolean"
        ? fixedOverride
        : Boolean(isFixedLayoutRef.current);
    const nextScale =
      typeof scaleOverride === "number" ? scaleOverride : scaleRef.current;

    const applyKey = `${nextTheme}|${nextFont}|${nextLineHeight}|${
      nextFixed ? "fixed" : nextScale
    }`;
    if (!force && lastEpubThemeApplyKeyRef.current === applyKey) return;
    lastEpubThemeApplyKeyRef.current = applyKey;

    applyEpubReaderTheme(target, {
      themeId: nextTheme,
      scale: nextScale,
      isFixedLayout: nextFixed,
      fontId: nextFont,
      lineHeight: nextLineHeight,
      extraRules: EPUB_HIGHLIGHT_THEME,
    });
  }, []);

  applyCurrentEpubThemeRef.current = applyCurrentEpubTheme;

  const pinchRef = useRef({
    isPinching: false,
    startDist: 0,
    startScale: 1,
    lastRatio: 1,
  });
  const pinchHandlersRef = useRef(null);
  const fontSizeThrottleRef = useRef(null);
  if (!pinchHandlersRef.current) {
    const getDistance = (touches) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );

    const isTouchTablet =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    const minScale = isTouchTablet ? 1.0 : 0.75;

    const applyFontSize = (newScale) => {
      applyCurrentEpubThemeRef.current?.(renditionRef.current, {
        scale: newScale,
        isFixedLayout: false,
        force: true,
      });
    };

    const THROTTLE_MS = 120;
    const applyFontSizeThrottled = (newScale) => {
      if (fontSizeThrottleRef.current) return;
      applyFontSize(newScale);
      fontSizeThrottleRef.current = setTimeout(() => {
        fontSizeThrottleRef.current = null;
      }, THROTTLE_MS);
    };

    pinchHandlersRef.current = {
      onTouchStart: (e) => {
        if (e.touches.length === 2) {
          const p = pinchRef.current;
          p.isPinching = true;
          p.startDist = getDistance(e.touches);
          p.startScale = scaleRef.current;
          p.lastRatio = 1;
        }
      },
      onTouchMove: (e) => {
        const p = pinchRef.current;
        if (p.isPinching && e.touches.length === 2) {
          if (e.cancelable) e.preventDefault();
          const dist = getDistance(e.touches);
          if (p.startDist > 0) {
            p.lastRatio = dist / p.startDist;
            const newScale = Math.max(
              minScale,
              Math.min(EPUB_MAX_SCALE, p.startScale * p.lastRatio),
            );
            if (!isFixedLayoutRef.current) {
              applyFontSizeThrottled(newScale);
            } else {
              const container = containerRef.current;
              if (container) {
                const clampedRatio = newScale / p.startScale;
                container.style.transform = `scale(${clampedRatio})`;
                container.style.transformOrigin = "center top";
              }
            }
          }
        }
      },
      onTouchEnd: (e) => {
        const p = pinchRef.current;
        if (p.isPinching && e.touches.length < 2) {
          p.isPinching = false;

          // Clear pending throttle
          if (fontSizeThrottleRef.current) {
            clearTimeout(fontSizeThrottleRef.current);
            fontSizeThrottleRef.current = null;
          }

          const newScale = Math.max(
            minScale,
            Math.min(EPUB_MAX_SCALE, p.startScale * p.lastRatio),
          );

          if (isFixedLayoutRef.current) {
            const container = containerRef.current;
            if (container) {
              container.style.transform = "";
              container.style.transformOrigin = "";
            }
          } else {
            applyFontSize(newScale);
          }

          if (Math.abs(newScale - scaleRef.current) > 0.02) {
            useReaderStore.getState().setScale(newScale);
          }
        }
      },
      onGestureStart: (e) => e.preventDefault(),
      onGestureChange: (e) => e.preventDefault(),
      minScale,
    };
  }

  const bindInternalLinksToChapters = useCallback((rendition) => {
    if (!rendition) return;

    try {
      rendition.hooks.content.register((contents) => {
        try {
          const doc = contents.document;
          if (!doc || doc.__igpLinksPatched) return;
          doc.__igpLinksPatched = true;

          const meta = useReaderStore.getState().metadata;
          const chapters =
            meta && Array.isArray(meta.chapters) ? meta.chapters : [];
          if (chapters.length === 0) return;

          const linkChapterMap = new WeakMap();
          const links = Array.from(doc.querySelectorAll("a[href]"));

          let currentChapterForGroup = null;

          const normalizeLabel = (raw) =>
            (raw || "")
              .trim()
              .replace(/^\d+\s*[.)]\s*/, "")
              .trim();

          const findChapterByLabel = (label) => {
            if (!label) return null;
            const normalizedLabel = label.toLowerCase();
            return (
              chapters.find((chapter) => {
                const title = (chapter.title || "").trim();
                if (!title) return false;

                const normalizedTitle = title.toLowerCase();
                if (normalizedTitle === normalizedLabel) return true;
                if (
                  normalizedTitle.startsWith(normalizedLabel) ||
                  normalizedLabel.startsWith(normalizedTitle)
                ) {
                  return true;
                }

                // Also allow exact match on raw title for safety
                if (title === label) return true;
                if (title.startsWith(label) || label.startsWith(title))
                  return true;

                return false;
              }) || null
            );
          };

          // Pre-scan links in document order and associate each link with
          // its parent/top-level chapter. The first matching chapter link
          // sets the "current group", and subsequent non-matching links
          // inherit that chapter as their parent.
          for (const link of links) {
            try {
              const href = link.getAttribute("href") || "";
              if (!href) continue;
              if (/^(https?:|mailto:|tel:)/i.test(href)) continue;

              const rawLabel = link.textContent || "";
              const label = normalizeLabel(rawLabel);
              const matchedChapter = findChapterByLabel(label);

              if (matchedChapter) {
                currentChapterForGroup = matchedChapter;
                linkChapterMap.set(link, matchedChapter);
              } else if (currentChapterForGroup) {
                linkChapterMap.set(link, currentChapterForGroup);
              }
            } catch {
              // Ignore individual link mapping failures
            }
          }

          const handleClick = (event) => {
            try {
              const target = event.target;
              if (!target) return;

              const link = target.closest && target.closest("a[href]");
              if (!link) return;

              const href = link.getAttribute("href") || "";
              if (!href) return;

              // Let external links behave normally
              if (/^(https?:|mailto:|tel:)/i.test(href)) {
                return;
              }

              const chapter =
                linkChapterMap.get(link) ||
                findChapterByLabel(
                  normalizeLabel((link.textContent || "").trim()),
                );
              if (!chapter) return;

              const startPage = Number(chapter.start_page) || 1;
              if (!startPage || startPage < 1) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();

              const store = useReaderStore.getState();
              store.setCurrentPage(startPage);
            } catch (err) {
              console.warn("Error handling internal TOC link click:", err);
            }
          };

          doc.addEventListener("click", handleClick);
        } catch (err) {
          console.warn("Error binding internal TOC links:", err);
        }
      });
    } catch (err) {
      console.warn("Error registering internal link hook:", err);
    }
  }, []);

  const bindPinchZoomToContents = useCallback((rendition) => {
    if (!rendition) return;
    const {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onGestureStart,
      onGestureChange,
    } = pinchHandlersRef.current;

    rendition.hooks.content.register((contents) => {
      try {
        const doc = contents.document;
        if (!doc) return;
        doc.documentElement.style.touchAction = "pan-x pan-y";
        doc.addEventListener("touchstart", onTouchStart, { passive: false });
        doc.addEventListener("touchmove", onTouchMove, { passive: false });
        doc.addEventListener("touchend", onTouchEnd);
        doc.addEventListener("gesturestart", onGestureStart, {
          passive: false,
        });
        doc.addEventListener("gesturechange", onGestureChange, {
          passive: false,
        });
      } catch {}
    });
  }, []);

  // Pinch-to-zoom on the outer container + Safari gesture prevention
  useEffect(() => {
    const isTouchCapable =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (!isTouchCapable || !containerRef.current) return;

    const container = containerRef.current;
    const {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onGestureStart,
      onGestureChange,
    } = pinchHandlersRef.current;

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("gesturestart", onGestureStart, {
      passive: false,
    });
    container.addEventListener("gesturechange", onGestureChange, {
      passive: false,
    });
    document.addEventListener("gesturestart", onGestureStart, {
      passive: false,
    });

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("gesturestart", onGestureStart);
      container.removeEventListener("gesturechange", onGestureChange);
      document.removeEventListener("gesturestart", onGestureStart);
    };
  }, []);

  // Apply scale for fixed-layout EPUBs using CSS transform
  const applyFixedLayoutScale = useCallback((rendition, scaleValue) => {
    if (!rendition) return;

    try {
      // Register a hook that applies transform scaling to all content as it loads
      rendition.hooks.content.register((contents) => {
        try {
          const body = contents.document.body;
          const html = contents.document.documentElement;

          if (body) {
            body.style.transformOrigin = "top left";
            body.style.transform = `scale(${scaleValue})`;
            body.style.width = `${100 / scaleValue}%`;
            body.style.height = `${100 / scaleValue}%`;
          }
          if (html) {
            html.style.transformOrigin = "top left";
            html.style.overflow = "hidden";
          }
        } catch (err) {
          console.warn("Error applying fixed-layout scale to contents:", err);
        }
      });
    } catch (err) {
      console.warn("Error registering fixed-layout scale hook:", err);
    }
  }, []);

  function bindSelectionHandlers(rendition) {
    if (!rendition) return;

    let isMouseDown = false;
    let isTouchInProgress = false;
    let lastSelectionTimestamp = 0;
    // Short-lived map to prevent duplicate create calls for the same CFI
    const recentSelectionMap = new Map();

    const processSelection = async (
      selection,
      contents,
      manualCfiRange = null,
      isInteractionEnd = false,
    ) => {
      // Only guard if we are actively dragging/touching AND it's not the final 'end' event
      if (!isInteractionEnd && (isMouseDown || isTouchInProgress)) return;

      if (!selection || selection.isCollapsed) return;
      const selectedTextStr = selection.toString().trim();
      if (!selectedTextStr) return;

      // THROTTLE: Prevent double trigger between mouseup and rendition events (300ms)
      if (Date.now() - lastSelectionTimestamp < 300) return;
      lastSelectionTimestamp = Date.now();

      const win = contents.window;
      let cfiRange = manualCfiRange;
      let range = null;

      try {
        range = selection.getRangeAt(0);
        if (!cfiRange) {
          cfiRange = contents.cfiFromRange(range);
        }
      } catch (err) {
        console.error("Error getting selection range/CFI:", err);
        if (!cfiRange) return;
      }

      // Desktop tool modes still auto-apply. On mobile, selection only opens
      // the bottom-docked toolbar — nothing is applied until the user acts.
      const allowDirectToolApply = !isMobileRef.current;

      // Duplicate-create guard for desktop tool auto-apply only.
      // Do NOT gate the mobile menu path — handle-drag updates must reopen/refresh it.
      if (allowDirectToolApply) {
        try {
          if (cfiRange) {
            const prev = recentSelectionMap.get(cfiRange);
            if (prev && Date.now() - prev < 1000) return;
            recentSelectionMap.set(cfiRange, Date.now());
            setTimeout(() => recentSelectionMap.delete(cfiRange), 1200);
          }
        } catch (e) {
          // ignore guard failures
        }
      }

      // 1. Handle Highlights Mode
      if (allowDirectToolApply && highlightEnabledRef.current) {
        await applyEpubHighlightSelection({
          selectionRange: range,
          selectionCfiRange: cfiRange,
          contents,
          selectedTextStr,
        });
      }
      // 2. Handle Underline Mode
      else if (allowDirectToolApply && underlineEnabledRef.current) {
        await applyEpubUnderlineSelection({
          selectionRange: range,
          selectionCfiRange: cfiRange,
          contents,
          selectedTextStr,
        });
      }
      // 3. Handle Notes Mode
      else if (
        allowDirectToolApply &&
        notesEnabledRef.current &&
        !highlightEnabledRef.current
      ) {
        setSelectionRect(null);
        let x = 0,
          y = 0;
        try {
          const rect = range.getBoundingClientRect();
          const iframeRect = win.frameElement.getBoundingClientRect();
          x = iframeRect.left + rect.left + rect.width / 2;
          y = iframeRect.top + rect.top;
        } catch {
          x = window.innerWidth / 2;
          y = window.innerHeight / 2;
        }

        setNotePosition({ x: isNaN(x) ? 100 : x, y: isNaN(y) ? 100 : y });
        setShowNoteInput(true);
        setNoteText("");
        setEditingNote({
          id: `temp-${Date.now()}`,
          content: "",
          page_number: currentPageRef.current ?? 1,
          selectedText: selectedTextStr,
          noteType: "selected",
          timestamp: new Date().toISOString(),
          isNew: true,
          coordinates: cfiRange ? { cfiRange } : undefined,
          chapter_number: currentChunkIndexRef.current ?? 1,
        });
      }
      // 4. Default (Show Context Menu) — always on mobile
      else {
        try {
          // Keep live Range for underline/highlight overlap checks; CFI is the
          // durable fallback when the Range is detached after menu actions.
          contextMenuRangeRef.current = range;
          contextMenuCfiRangeRef.current = cfiRange;
          contextMenuContentsRef.current = contents;

          const rect = range.getBoundingClientRect();
          const iframeRect = win.frameElement.getBoundingClientRect();
          // Guard against zero/degenerate rect (can happen on touch during handle drag)
          const hasValidRect = rect.width > 0 || rect.height > 0;
          const x = hasValidRect
            ? iframeRect.left + rect.left + rect.width / 2
            : window.innerWidth / 2;
          const y = hasValidRect
            ? iframeRect.top + rect.bottom + 10
            : window.innerHeight / 3;

          setSelectedText(selectedTextStr);
          if (hasValidRect) {
            setSelectionRect({
              top: iframeRect.top + rect.top,
              bottom: iframeRect.top + rect.bottom,
              left: iframeRect.left + rect.left,
              right: iframeRect.left + rect.right,
              width: rect.width,
              height: rect.height,
            });
          } else {
            setSelectionRect(null);
          }
          setContextMenuPosition({ x, y });
          setContextMenuVisible(true);
        } catch (err) {
          console.error("Error opening context menu:", err);
        }
      }
    };

    rendition.hooks.content.register((contents) => {
      const doc = contents.document;
      const win = contents.window;

      // Desktop/Mouse Logic: Fast (20ms delay)
      const handleMouseUpLocal = () => {
        isMouseDown = false;
        if (isTouchInProgress) return;
        // Force process on end of interaction
        setTimeout(() => {
          processSelection(win.getSelection(), contents, null, true);
        }, 20);
      };

      const handleMouseDownLocal = () => {
        isMouseDown = true;
      };

      // iPad/Touch Logic: Debounced (600ms)
      let selectionChangeTimer;
      const handleSelectionChange = () => {
        // If mouse is down (desktop), ignore selectionchange to avoid instant highlights
        if (isMouseDown && !isTouchInProgress) return;

        clearTimeout(selectionChangeTimer);
        selectionChangeTimer = setTimeout(() => {
          const selection = win.getSelection();
          if (selection && !selection.isCollapsed) {
            processSelection(selection, contents);
          }
        }, 600);
      };

      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;

      doc.addEventListener(
        "touchstart",
        () => {
          isTouchInProgress = true;
          isMouseDown = true;
        },
        { passive: true },
      );

      doc.addEventListener(
        "touchend",
        () => {
          isTouchInProgress = false;
          isMouseDown = false;
          // IMPORTANT: Force trigger on touch end to catch state browser might miss
          setTimeout(() => {
            processSelection(win.getSelection(), contents, null, true);
          }, 100);
        },
        { passive: true },
      );

      doc.addEventListener("mousedown", handleMouseDownLocal);
      doc.addEventListener("mouseup", handleMouseUpLocal);

      // Enforce clipboard copy policy: allowed flag and max character limit (EPUB iframe)
      const handleCopy = (e) => {
        const policy = useReaderStore.getState().drmPolicy;
        if (!isCopyAllowed(policy)) {
          e.preventDefault();
          showCopyNotAllowedNotification();
          return;
        }
        const limit = getCopyCharLimit(policy);
        if (limit === -1) return; // unlimited copy allowed
        const selection = win.getSelection();
        const text = selection ? selection.toString().trim() : "";
        if (text.length <= limit) return;
        e.preventDefault();
        showCopyLimitExceededNotification(limit);
        const toCopy = truncateForCopy(text, limit);
        if (e.clipboardData) {
          e.clipboardData.setData("text/plain", toCopy);
        }
      };
      doc.addEventListener("copy", handleCopy);

      // Only use selectionchange for touch devices to support drag handles
      // This prevents the instant selection issue on desktop mouse drags
      if (isTouchDevice) {
        doc.addEventListener("selectionchange", handleSelectionChange);
      }

      // Mobile / coarse: lock epub.js column + iframe scroll while selecting so
      // long-press / handle-drag does not pan to another part of the chapter.
      if (
        isTouchDevice &&
        shouldGuardEpubSelectionPan(isMobileRef.current)
      ) {
        attachEpubSelectionScrollLock({
          doc,
          win,
          getManagerContainer: () =>
            renditionRef.current?.manager?.container ||
            rendition?.manager?.container ||
            null,
        });
      }

      // Click-to-Point Note Logic
      doc.addEventListener("click", (e) => {
        const clickedImage = e.target?.closest?.("img");
        if (clickedImage) {
          const imageSrcRaw =
            clickedImage.currentSrc || clickedImage.getAttribute("src") || "";
          if (imageSrcRaw) {
            // Ensure relative EPUB image paths resolve correctly for the
            // fullscreen overlay rendered in the parent document.
            let imageSrc = imageSrcRaw;
            try {
              imageSrc = new URL(imageSrcRaw, win.location.href).href;
            } catch {
              // If URL parsing fails, fall back to the raw value.
            }
            e.preventDefault();
            e.stopPropagation();
            openImageLightbox(
              imageSrc,
              clickedImage.getAttribute("alt") || t("epub.imageAlt"),
            );
          }
          return;
        }

        if (
          notesEnabledRef.current &&
          !highlightEnabledRef.current &&
          !underlineEnabledRef.current
        ) {
          const selection = win.getSelection();
          if (selection && !selection.isCollapsed) return;
          e.stopPropagation();

          const iframeRect = win.frameElement.getBoundingClientRect();
          setNotePosition({
            x: iframeRect.left + e.clientX,
            y: iframeRect.top + e.clientY,
          });
          setShowNoteInput(true);
          setNoteText("");
          // Capture viewport CFI now so navigation still works if the user
          // pages away before saving.
          const locationCfi = getCurrentLocationCfi();
          setEditingNote({
            id: `temp-${Date.now()}`,
            content: "",
            page_number: currentPageRef.current ?? 1,
            selectedText: "",
            noteType: "page",
            timestamp: new Date().toISOString(),
            isNew: true,
            coordinates: locationCfi ? { cfiRange: locationCfi } : undefined,
            chapter_number: currentChunkIndexRef.current ?? 1,
          });
        }
      });

      // Context Menu Handler
      const handleContextMenuLocal = (e) => {
        e.preventDefault();
        const selection = win.getSelection();
        const selectedTextStr = selection ? selection.toString().trim() : "";
        if (selectedTextStr) {
          try {
            const range = selection.getRangeAt(0);
            contextMenuRangeRef.current = range;
            contextMenuCfiRangeRef.current = contents.cfiFromRange(range);
            contextMenuContentsRef.current = contents;
          } catch {}

          const iframeRect = win.frameElement.getBoundingClientRect();
          setSelectedText(selectedTextStr);
          setContextMenuPosition({
            x: iframeRect.left + e.clientX,
            y: iframeRect.top + e.clientY,
          });
          setContextMenuVisible(true);
        }
      };

      doc.addEventListener("contextmenu", handleContextMenuLocal, true);
      win.addEventListener("contextmenu", handleContextMenuLocal, true);

      // Forward keyboard events to the parent window so global shortcuts work
      const handleForwardKeyboardEvents = (e) => {
        if (e.defaultPrevented) return;
        const target = e.target;
        const isInput =
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.isContentEditable);
        if (isInput) return;

        try {
          const eventClone = new KeyboardEvent(e.type, {
            key: e.key,
            code: e.code,
            location: e.location,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey,
            altKey: e.altKey,
            metaKey: e.metaKey,
            repeat: e.repeat,
            isComposing: e.isComposing,
            bubbles: true,
            cancelable: true,
          });
          const targetElement =
            window.document.activeElement || window.document.body;
          const wasNotPrevented = targetElement.dispatchEvent(eventClone);
          if (!wasNotPrevented || eventClone.defaultPrevented) {
            e.preventDefault();
          }
        } catch (err) {
          console.warn("Could not forward keyboard event:", err);
        }
      };

      doc.addEventListener("keydown", handleForwardKeyboardEvents);
      doc.addEventListener("keyup", handleForwardKeyboardEvents);

      // Block DevTools keyboard shortcuts inside EPUB iframe
      const handleDevToolsBlock = (e) => {
        // F12 - Open DevTools
        if (e.key === "F12") {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+Shift+I (Windows/Linux) or Cmd+Option+I (Mac) - Inspect Element
        if (
          (e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          e.key.toLowerCase() === "i"
        ) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+Shift+J (Windows/Linux) or Cmd+Option+J (Mac) - Console
        if (
          (e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          e.key.toLowerCase() === "j"
        ) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+Shift+C (Windows/Linux) or Cmd+Option+C (Mac) - Inspect Element picker
        if (
          (e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          e.key.toLowerCase() === "c"
        ) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+U (Windows/Linux) or Cmd+U (Mac) - View Source
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "u") {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      doc.addEventListener("keydown", handleDevToolsBlock, true);
      win.addEventListener("keydown", handleDevToolsBlock, true);

      const clearAnnotationSelectionChrome = (e) => {
        const target = e?.target;
        const isAnnotationTarget =
          target instanceof Element &&
          Boolean(
            target.closest?.(
              '[class*="epub-highlight"], [class*="epub-underline"], .epubjs-hl, .epubjs-ul',
            ),
          );

        // Annotation taps are handled by epub.js handlers; don't race-clear here.
        if (
          isAnnotationTarget ||
          Date.now() < suppressAnnotationMenuCloseUntilRef.current
        ) {
          return;
        }

        // Active text selection (including iOS/Android handle drags): only hide
        // the toolbar so it doesn't cover handles. Keep selection refs so
        // touchend / selectionchange can refresh the menu with the new range.
        const liveSelection = win.getSelection?.();
        const hasLiveTextSelection = Boolean(
          liveSelection &&
            !liveSelection.isCollapsed &&
            liveSelection.toString().trim(),
        );
        if (hasLiveTextSelection) {
          if (contextMenuVisibleRef.current) {
            setContextMenuVisible(false);
          }
          return;
        }

        setSelectedHighlightOverlay(null);
        setSelectedUnderlineOverlay(null);
        setSelectionRect(null);
        if (contextMenuVisibleRef.current) {
          setContextMenuVisible(false);
          setSelectedText("");
          contextMenuRangeRef.current = null;
          contextMenuCfiRangeRef.current = null;
          contextMenuContentsRef.current = null;
        }
      };

      doc.addEventListener("mousedown", (e) => {
        handleMouseDownLocal();
        clearAnnotationSelectionChrome(e);
      });
      // Mobile: dismiss annotation chrome on tap inside the iframe without
      // waiting for the delayed synthetic mousedown. Handle-drags are preserved
      // above when a live text selection is still active.
      doc.addEventListener("touchstart", clearAnnotationSelectionChrome, {
        passive: true,
      });
    });

    // Rendition event fallback
    rendition.on("selected", (cfiRange, contents) => {
      // Only use fallback if not actively interacting
      if (!isMouseDown && !isTouchInProgress) {
        processSelection(contents.window.getSelection(), contents, cfiRange);
      }
    });
  }

  useEffect(() => {
    if (!epubBook || !containerRef.current || isInitializedRef.current) return;

    const container = containerRef.current;
    let resizeTimeout;
    let handleResize;
    const myMountId = mountIdRef.current;

    const initializeEpub = async () => {
      try {
        setIsRendering(true);
        setError(null);
        if (isInitializedRef.current) return;

        if (renditionRef.current) {
          try {
            // Destroy existing rendition first to let epubjs clean up
            // its iframes and listeners before we clear the container.
            renditionRef.current.destroy();
          } catch (error) {
            console.error("Error destroying existing rendition:", error);
          }
          renditionRef.current = null;
        }

        if (container) {
          container.innerHTML = "";
          const existingElements = container.querySelectorAll("[data-epub]");
          existingElements.forEach((el) => el.remove());
        }

        await new Promise((resolve) => setTimeout(resolve, 200));

        if (!epubBook.packaging || !epubBook.spine) {
          throw new Error("EPUB book not fully loaded");
        }

        container.setAttribute("data-epub-container", "true");
        container.id = "epub-container-" + Date.now();

        // Get container dimensions for paginated layout
        const containerWidth = container.clientWidth || window.innerWidth;
        const containerHeight =
          container.clientHeight || window.innerHeight - 100;

        // Determine spread setting based on current pageViewMode + DRM cover isolation
        // Double page mode uses "auto" spread, single page uses "none"
        const currentState = useReaderStore.getState();
        const currentPageViewMode = currentState.pageViewMode;
        const currentReadingDirection =
          currentState.readingDirection || readingDirection;
        const initPage =
          Number(currentState.initialViewStartPage) ||
          Number(currentState.currentPage) ||
          1;

        // Only allow double page mode on large devices (>1024px).
        // This stops iPads and small laptops from entering double page mode, tracking touch capability to absolutely block iPad pros
        const initAllowDouble = containerWidth > 1024 && !isTouchTablet;
        const spreadValue = resolveEpubSpreadValue(
          initAllowDouble,
          currentPageViewMode,
          currentState.drmPolicy,
          initPage,
        );

        // Set package page-progression before renderTo so epub.js start() picks it up.
        void applyEpubReadingDirection(epubBook, currentReadingDirection);

        const rendition = epubBook.renderTo(container, {
          width: containerWidth,
          height: containerHeight,
          spread: spreadValue,
          // Minimum width to use spreads
          minSpreadWidth: spreadValue === "auto" ? 1024 : 800,
          // Allow scripted content inside EPUB iframes so embedded scripts
          // (e.g. interactive EPUBs) can run. This also prevents blocked
          // script errors from the browser when epubjs injects srcdoc frames.
          allowScriptedContent: true,
          allowPopups: true,
          manager: "default",
          // Use paginated flow for horizontal page-by-page navigation
          flow: "paginated",
          resizeOnOrientationChange: true,
          defaultDirection: currentReadingDirection,
        });

        rendition._mountId = myMountId;
        renditionRef.current = rendition;
        currentChunkBookRef.current = epubBook;
        currentChunkIndexRef.current = initialChapterNumber;
        setCurrentChunkIndex(initialChapterNumber);
        setEpubCurrentChapterNumber(initialChapterNumber);
        setEpubRendition(rendition);

        await rendition.ready;
        // Keep Layout.settings.direction aligned (epub.js does not always).
        syncEpubLayoutDirection(rendition, currentReadingDirection);

        // Detect EPUB version and layout type
        try {
          const packaging = epubBook.packaging;
          const version =
            packaging?.metadata?.version || packaging?.version || "unknown";
          const layout = packaging?.metadata?.layout || "reflowable";
          const isFixed = layout === "pre-paginated" || layout === "fixed";

          setIsFixedLayout(isFixed);
          // Sync ref before patchRendition applies theme (state update is async).
          isFixedLayoutRef.current = isFixed;

          // Log EPUB info for debugging
          devLog(
            `📚 EPUB v${version} | Layout: ${isFixed ? "Fixed (pre-paginated)" : "Reflowable"}`,
          );

          if (isFixed) {
            applyFixedLayoutScale(rendition, scale);
          }
        } catch (err) {
          console.warn("Could not detect EPUB layout type:", err);
          setIsFixedLayout(false);
          isFixedLayoutRef.current = false;
        }

        // Patch and bind selection (also applies content theme once)
        patchRendition(rendition);
        bindInternalLinksToChapters(rendition);

        // Bind selection handlers BEFORE display(0) so hooks apply to first content
        bindSelectionHandlers(rendition);
        bindPinchZoomToContents(rendition);

        handleResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            try {
              const container = containerRef.current;
              if (container && renditionRef.current) {
                const width = container.clientWidth || window.innerWidth;
                const height =
                  container.clientHeight || window.innerHeight - 100;
                renditionRef.current.resize(width, height);
              }
            } catch (error) {}
          }, 100);
        };

        window.addEventListener("resize", handleResize);

        if (rendition?.book) {
          await rendition.display(0);
        }

        const initialChapter = getChapterByNumber(
          metadata,
          initialChapterNumber,
        );
        if (initialChapter) {
          const initialChapterNumberVal =
            Number(initialChapter.chapter_number) || 1;
          setCurrentChunkIndex(initialChapterNumberVal);
          currentChunkIndexRef.current = initialChapterNumberVal;

          const storeState = useReaderStore.getState();
          const pendingPage =
            storeState.initialViewStartPage != null
              ? Number(storeState.initialViewStartPage)
              : storeState.currentPage;
          const hasExplicitStartPage =
            Number.isFinite(pendingPage) &&
            pendingPage > 1 &&
            !isLegacySavedEpubPage(pendingPage, metadata);

          if (!hasExplicitStartPage) {
            const initialSyntheticPage =
              getChapterPageBounds(initialChapter).startPage;
            if (
              currentPageRef.current == null ||
              isLegacySavedEpubPage(currentPageRef.current, metadata)
            ) {
              skipCurrentPageSyncRef.current = true;
              setCurrentPage(initialSyntheticPage);
            }
          }
        }

        // Set initial chapter position state
        setAtChapterStart(true);
        atChapterStartRef.current = true;
        setAtChapterEnd(false);
        atChapterEndRef.current = false;
        setCurrentSection(0);

        if (documentId) {
          const storeState = useReaderStore.getState();
          const skipLocalRestore =
            storeState.initialViewStartPage != null ||
            (Number(storeState.currentPage) > 1 &&
              !isLegacySavedEpubPage(storeState.currentPage, metadata));

          const localRaw = skipLocalRestore
            ? null
            : localStorage.getItem(`igp_reader_state_${documentId}`);
          if (localRaw) {
            try {
              const readerState = JSON.parse(localRaw);
              const savedPage = Number(readerState.currentPage);
              const maxPage =
                Number(metadata?.total_pages) > 0
                  ? metadata.total_pages
                  : epubBook.spine?.length || 1;
              if (
                savedPage >= 1 &&
                savedPage <= maxPage &&
                !isLegacySavedEpubPage(savedPage, metadata)
              ) {
                devLog(
                  `📖 Restoring EPUB to page ${savedPage} (of ${maxPage})`,
                );
                setCurrentPage(savedPage);
              }
              if (readerState.scale) {
                const savedScale = Number(readerState.scale);
                if (!isNaN(savedScale)) {
                  const isTouchTablet =
                    typeof window !== "undefined" &&
                    ("ontouchstart" in window || navigator.maxTouchPoints > 0);
                  const minScale = isTouchTablet ? 1.0 : 0.75;
                  const validScale = Math.max(
                    minScale,
                    Math.min(EPUB_MAX_SCALE, savedScale),
                  );
                  setScale(validScale);
                }
              }
            } catch (err) {
              console.error(
                "Error restoring EPUB position from localStorage:",
                err,
              );
            }
          }
        }

        isInitializedRef.current = true;

        // Note: bindSelectionHandlers and bindPinchZoomToContents called above before display(0)

        let relocatedRaf = null;
        rendition.on("relocated", (location) => {
          if (relocatedRaf) cancelAnimationFrame(relocatedRaf);
          relocatedRaf = requestAnimationFrame(() => {
            try {
              const localSectionIndex = getCurrentSectionIndex(rendition);
              setCurrentSection(localSectionIndex);

              // Track if we're at the start or end of the current chapter for page navigation
              const { atStart, atEnd } = deriveChapterBoundsFromLocation(
                rendition,
                location,
              );
              setAtChapterStart(atStart);
              atChapterStartRef.current = atStart;
              setAtChapterEnd(atEnd);
              atChapterEndRef.current = atEnd;

              let nextPage = localSectionIndex + 1;
              if (metadata && Array.isArray(metadata.chapters)) {
                const currentChapter = metadata.chapters.find(
                  (c) =>
                    Number(c.chapter_number) === currentChunkIndexRef.current,
                );
                if (currentChapter) {
                  nextPage = getSyntheticPageFromLocation(
                    currentChapter,
                    rendition,
                    location,
                    "start",
                  );
                  const nextPageEnd = getSyntheticPageFromLocation(
                    currentChapter,
                    rendition,
                    location,
                    "end",
                  );
                  if (isSeekingRef.current) return;
                  if (currentPageRef.current !== nextPage) {
                    skipCurrentPageSyncRef.current = true;
                  }
                  setCurrentPageWithEnd(nextPage, nextPageEnd);
                } else {
                  if (currentPageRef.current !== nextPage) {
                    skipCurrentPageSyncRef.current = true;
                    setCurrentPage(nextPage);
                  }
                }
              }
            } catch (error) {
              console.error("Error in relocated handler:", error);
            }
          });
        });

        setEpubReady(true);

        const storeAfterInit = useReaderStore.getState();
        const pageToNavigate =
          storeAfterInit.initialViewStartPage != null
            ? Number(storeAfterInit.initialViewStartPage)
            : Number(storeAfterInit.currentPage);
        if (
          pageToNavigate > 1 &&
          !isLegacySavedEpubPage(pageToNavigate, metadata)
        ) {
          skipCurrentPageSyncRef.current = false;
          currentPageRef.current = pageToNavigate;
          setCurrentPage(pageToNavigate);
          requestPageNavigation();
        }
      } catch (error) {
        console.error("Error initializing EPUB:", error);
        setError(error.message);
      } finally {
        setIsRendering(false);
      }
    };

    initializeEpub();

    return () => {
      isInitializedRef.current = false;
      setEpubReady(false);
      initialViewAppliedRef.current = false;
      resetSearchHighlightTracking();
      const currentRendition = renditionRef.current;
      try {
        if (currentRendition && currentRendition._mountId === myMountId) {
          const res = currentRendition.destroy();
          renditionRef.current = null;
          // Clear rendition reference from global store
          setEpubRendition(null);
          if (res && res.then) {
            res.catch((err) =>
              console.error("Error destroying rendition async:", err),
            );
          }
        } else {
          renditionRef.current = null;
          setEpubRendition(null);
        }
      } catch (error) {
        console.error("Error destroying rendition:", error);
        renditionRef.current = null;
        setEpubRendition(null);
      }

      if (container) {
        try {
          container.innerHTML = "";
          container.removeAttribute("data-epub-container");
        } catch (err) {}
      }

      if (handleResize) {
        window.removeEventListener("resize", handleResize);
      }
      clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    epubBook,
    // NOTE: Do NOT include scale here - it should not trigger re-initialization
    // Scale changes are handled by a separate effect that updates the existing rendition
    setEpubRendition,
    setCurrentPage,
    setEpubCurrentChapterNumber,
    metadata,
    initialChapterNumber,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    return () => {
      isInitializedRef.current = false;
      if (renditionRef.current) {
        try {
          renditionRef.current.destroy();
        } catch (error) {
          console.error("Error destroying rendition on unmount:", error);
        }
        renditionRef.current = null;
        setEpubRendition(null);
      }
      if (container) {
        container.innerHTML = "";
        container.removeAttribute("data-epub-container");
      }
    };
  }, [setEpubRendition]);

  useEffect(() => {
    if (skipCurrentPageSyncRef.current) {
      skipCurrentPageSyncRef.current = false;
      return;
    }

    if (
      !renditionRef.current ||
      !epubBook ||
      isRenderingRef.current ||
      !epubReady
    )
      return;

    const navigateToSection = async () => {
      try {
        const hasTotalPages = Number(metadata?.total_pages) > 0;
        const maxSections =
          metadata && hasTotalPages
            ? metadata.total_pages
            : epubBook.spine?.length || 0;

        if (maxSections === 0) return;

        const targetPage = Math.max(1, Math.min(currentPage, maxSections));
        let targetChapter = null;

        if (metadata && Array.isArray(metadata.chapters)) {
          // Find the chapter that contains the target page
          targetChapter = metadata.chapters.find((c) => {
            const startPage = Number(c.start_page) || 1;
            const endPage = Number(c.end_page) || 1;
            return targetPage >= startPage && targetPage <= endPage;
          });

          if (!targetChapter) {
            console.warn(`No chapter found for page ${targetPage}`);
            return;
          }

          const targetChapterNumber = Number(targetChapter.chapter_number) || 1;
          const currentChapterNumber = currentChunkIndexRef.current;

          // If we need a different chapter, load it and render
          if (targetChapterNumber !== currentChapterNumber) {
            setIsRendering(true);

            try {
              // Load the target chapter
              const chapterBook = await getEpubChapterForPage(targetPage);
              if (!chapterBook) {
                console.error("Failed to load chapter", targetChapterNumber);
                setIsRendering(false);
                return;
              }

              // Destroy current rendition and clean container
              const container = containerRef.current;
              if (!container) {
                setIsRendering(false);
                return;
              }

              if (renditionRef.current) {
                try {
                  // Destroy rendition BEFORE clearing container to allow cleanup
                  await renditionRef.current.destroy();
                } catch (err) {
                  console.error("Error destroying rendition:", err);
                }
                renditionRef.current = null;
                // Old CFIs are invalid on the new rendition — reset tracking
                resetSearchHighlightTracking();
              }

              // Force React to recreate the container by changing the key
              // This prevents React from interfering with epub.js DOM manipulation
              setContainerKey((prev) => prev + 1);

              // Wait for React to render the new container
              // Use a longer timeout and check if container is actually ready
              let newContainer = null;
              let attempts = 0;
              while (!newContainer && attempts < 10) {
                await new Promise((resolve) => setTimeout(resolve, 50));
                newContainer = containerRef.current;
                attempts++;
              }

              if (!newContainer) {
                console.error("New container not available after key change");
                setIsRendering(false);
                return;
              }

              // Get container dimensions for paginated layout
              const containerWidth =
                newContainer.clientWidth || window.innerWidth;
              const containerHeight =
                newContainer.clientHeight || window.innerHeight - 100;

              // Determine spread setting based on current pageViewMode + DRM cover isolation
              const currentState = useReaderStore.getState();
              const currentPageViewMode = currentState.pageViewMode;
              const currentReadingDirection =
                currentState.readingDirection || readingDirection;
              const initAllowDouble = containerWidth > 1024 && !isTouchTablet;
              const spreadValue = resolveEpubSpreadValue(
                initAllowDouble,
                currentPageViewMode,
                currentState.drmPolicy,
                Number(currentState.currentPage) || 1,
              );

              // Keep chapter chunks on the same page-progression as the open book.
              void applyEpubReadingDirection(chapterBook, currentReadingDirection);

              const rendition = chapterBook.renderTo(newContainer, {
                width: containerWidth,
                height: containerHeight,
                spread: spreadValue,
                // Minimum width to use spreads
                minSpreadWidth: spreadValue === "auto" ? 1024 : 800,
                allowScriptedContent: true,
                allowPopups: true,
                manager: "default",
                flow: "paginated",
                resizeOnOrientationChange: true,
                defaultDirection: currentReadingDirection,
              });

              rendition._mountId = mountIdRef.current;
              renditionRef.current = rendition;
              currentChunkBookRef.current = chapterBook;
              currentChunkIndexRef.current = targetChapterNumber;
              setEpubRendition(rendition);

              await rendition.ready;
              syncEpubLayoutDirection(rendition, currentReadingDirection);

              // Patch and bind selection + pinch-to-zoom
              patchRendition(rendition);
              bindInternalLinksToChapters(rendition);
              bindSelectionHandlers(rendition);
              bindPinchZoomToContents(rendition);

              // Note: No need to clear annotations — this is a freshly created
              // rendition with no leftover annotations from the previous chapter.
              // Search highlight tracking was already reset via
              // resetSearchHighlightTracking() when the old rendition was destroyed.

              // Apply theme
              // Using patchRendition above, no need for separate theme call here

              // Bind selection - Using bindSelectionHandlers above

              // Bind relocated handler (throttled via rAF to avoid scroll fights)
              let chapterRelocatedRaf = null;
              rendition.on("relocated", (location) => {
                if (chapterRelocatedRaf)
                  cancelAnimationFrame(chapterRelocatedRaf);
                chapterRelocatedRaf = requestAnimationFrame(() => {
                  try {
                    const localSectionIndex = getCurrentSectionIndex(rendition);
                    setCurrentSection(localSectionIndex);

                    // Track if we're at the start or end of the current chapter
                    const { atStart, atEnd } = deriveChapterBoundsFromLocation(
                      rendition,
                      location,
                    );
                    setAtChapterStart(atStart);
                    atChapterStartRef.current = atStart;
                    setAtChapterEnd(atEnd);
                    atChapterEndRef.current = atEnd;

                    const nextPage = getSyntheticPageFromLocation(
                      targetChapter,
                      rendition,
                      location,
                      "start",
                    );
                    const nextPageEnd = getSyntheticPageFromLocation(
                      targetChapter,
                      rendition,
                      location,
                      "end",
                    );
                    if (isSeekingRef.current) return;
                    if (currentPageRef.current !== nextPage) {
                      skipCurrentPageSyncRef.current = true;
                    }
                    setCurrentPageWithEnd(nextPage, nextPageEnd);
                  } catch (error) {
                    console.error("Error in relocated handler:", error);
                  }
                });
              });

              // Reset chapter position tracking
              setAtChapterStart(true);
              atChapterStartRef.current = true;
              setAtChapterEnd(false);
              atChapterEndRef.current = false;

              await displayCfiOrSeekToSyntheticPage(
                rendition,
                targetChapter,
                targetPage,
                isSeekingRef,
                pendingDisplayCfiRef,
              );

              await new Promise((resolve) => setTimeout(resolve, 100));

              setCurrentChunkIndex(targetChapterNumber);
              currentChunkIndexRef.current = targetChapterNumber;
              setEpubCurrentChapterNumber(targetChapterNumber);
              setCurrentSection(getCurrentSectionIndex(rendition));
              // Do NOT call setCurrentPage here — the relocated handler
              // (via setCurrentPageWithEnd) already sets the correct page
              // based on hardware geometry. Calling setCurrentPage(targetPage)
              // would fight the relocated handler and trigger a re-entrancy
              // loop in the navigateToSection useEffect.

              // Re-apply search highlights on the freshly created rendition
              // if search is currently active.  The periodic effect will also
              // fire, but this guarantees highlights appear immediately.
              const storeState = useReaderStore.getState();
              if (
                storeState.searchResults?.length > 0 &&
                storeState.isSearchSidebarOpen
              ) {
                applyEpubSearchHighlights(
                  rendition,
                  storeState.searchResults,
                  targetChapterNumber,
                );
              }
            } catch (err) {
              console.error("Error switching to chapter:", err);
              if (
                err.response?.status === 401 ||
                err.message?.includes("401")
              ) {
                showSessionExpiredOnce();
              }
            } finally {
              setIsRendering(false);
            }
            return;
          }
        }

        // Same chapter — only navigate if section actually differs
        const relativeSectionIndex = metadata
          ? getRelativeSectionIndex(targetPage - 1)
          : targetPage - 1;

        if (!metadata && currentSectionRef.current === relativeSectionIndex) {
          return;
        }

        const rendition = renditionRef.current;
        if (rendition?.book && metadata && targetChapter) {
          await displayCfiOrSeekToSyntheticPage(
            rendition,
            targetChapter,
            targetPage,
            isSeekingRef,
            pendingDisplayCfiRef,
          );
        } else if (rendition?.book) {
          const pendingCfi = pendingDisplayCfiRef.current;
          if (pendingCfi) {
            pendingDisplayCfiRef.current = null;
            await rendition.display(pendingCfi);
          } else {
            await rendition.display(relativeSectionIndex);
          }
        }
        setCurrentSection(getCurrentSectionIndex(rendition));
      } catch (error) {
        console.error("Error navigating to section:", error);
      }
    };

    navigateToSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    epubBook,
    epubReady,
    pageNavigationRequest,
    metadata,
    getEpubChapterForPage,
    getRelativeSectionIndex,
    // NOTE: Do NOT include scale, currentSection, or isRendering here — they
    // should not trigger re-navigation. isRendering is checked via ref.
    setEpubRendition,
    setEpubCurrentChapterNumber,
    setCurrentPage,
    showSessionExpiredOnce,
  ]);

  useEffect(() => {
    if (renditionRef.current && !isRendering) {
      try {
        if (isFixedLayout) {
          // For fixed-layout EPUBs, apply CSS transform scaling directly to existing iframes
          const views = renditionRef.current.views();
          if (views && views.displayed) {
            Object.values(views.displayed).forEach((view) => {
              try {
                const contents = view.contents;
                if (contents && contents.document) {
                  const body = contents.document.body;
                  const html = contents.document.documentElement;

                  if (body) {
                    body.style.transformOrigin = "top left";
                    body.style.transform = `scale(${scale})`;
                    body.style.width = `${100 / scale}%`;
                    body.style.height = `${100 / scale}%`;
                  }
                  if (html) {
                    html.style.transformOrigin = "top left";
                    html.style.overflow = "hidden";
                  }
                }
              } catch (err) {
                console.warn("Error applying fixed-layout scale to view:", err);
              }
            });
          }

          // Register hook for future content loads
          renditionRef.current.hooks.content.register((contents) => {
            try {
              const body = contents.document.body;
              const html = contents.document.documentElement;

              if (body) {
                body.style.transformOrigin = "top left";
                body.style.transform = `scale(${scale})`;
                body.style.width = `${100 / scale}%`;
                body.style.height = `${100 / scale}%`;
              }
              if (html) {
                html.style.transformOrigin = "top left";
                html.style.overflow = "hidden";
              }
            } catch (err) {
              console.warn("Error applying fixed-layout scale in hook:", err);
            }
          });
        } else {
          // Reflowable: theme + font-size (deduped if theme effect already applied)
          applyCurrentEpubTheme(renditionRef.current, {
            scale,
            isFixedLayout: false,
          });

          if (epubAnnotationRefreshRafRef.current) {
            cancelAnimationFrame(epubAnnotationRefreshRafRef.current);
          }
          epubAnnotationRefreshRafRef.current = requestAnimationFrame(() => {
            epubAnnotationRefreshRafRef.current = null;
            refreshRenderedEpubAnnotations();

            // After font-size reflow, force a relocated event so page range
            // numbers update immediately (scrollWidth and physical screen
            // count change after reflow, but epub.js doesn't always fire
            // relocated automatically).
            requestAnimationFrame(() => {
              const rendition = renditionRef.current;
              if (rendition && typeof rendition.reportLocation === "function") {
                rendition.reportLocation();
              }
            });
          });
        }
      } catch (error) {
        console.error("Error applying scale:", error);
      }
    }
    return () => {
      if (epubAnnotationRefreshRafRef.current) {
        cancelAnimationFrame(epubAnnotationRefreshRafRef.current);
        epubAnnotationRefreshRafRef.current = null;
      }
    };
  }, [
    scale,
    isRendering,
    isFixedLayout,
    applyCurrentEpubTheme,
    refreshRenderedEpubAnnotations,
  ]);

  // Content theme / font / line-height — CSS only; no annotation API calls.
  // After typography reflow, reposition SVG marks (same as font-size/scale path).
  useEffect(() => {
    if (!renditionRef.current || isRendering) return;
    try {
      applyCurrentEpubTheme(renditionRef.current, {
        themeId: theme,
        fontId: epubFontFamily,
        lineHeight: epubLineHeight,
      });
    } catch (error) {
      console.error("Error applying EPUB content theme:", error);
      return undefined;
    }

    if (epubAnnotationRefreshRafRef.current) {
      cancelAnimationFrame(epubAnnotationRefreshRafRef.current);
    }
    epubAnnotationRefreshRafRef.current = requestAnimationFrame(() => {
      epubAnnotationRefreshRafRef.current = null;
      // Second frame: layout/metrics settle after stylesheet + override apply
      requestAnimationFrame(() => {
        refreshRenderedEpubAnnotations();
        const rendition = renditionRef.current;
        if (rendition && typeof rendition.reportLocation === "function") {
          rendition.reportLocation();
        }
      });
    });

    return () => {
      if (epubAnnotationRefreshRafRef.current) {
        cancelAnimationFrame(epubAnnotationRefreshRafRef.current);
        epubAnnotationRefreshRafRef.current = null;
      }
    };
  }, [
    theme,
    epubFontFamily,
    epubLineHeight,
    isRendering,
    applyCurrentEpubTheme,
    refreshRenderedEpubAnnotations,
  ]);

  // Scroll-to-load is disabled; chunk navigation is manual via buttons.

  // Note handling functions

  const handleNoteIndicatorClick = (note, e) => {
    e.stopPropagation();
    if (!note) return;

    const indicatorRect = e.currentTarget.getBoundingClientRect();
    let x = indicatorRect.left - 280;
    let y = indicatorRect.bottom + 10;

    if (x < 20) x = 20;
    if (x > window.innerWidth - 320) x = window.innerWidth - 320;
    if (y > window.innerHeight - 200) y = window.innerHeight - 200;
    if (y < 60) y = 60;

    const noteIndex = notes.findIndex((n) => n && n.id === note.id);
    if (noteIndex !== -1) {
      setCurrentNoteIndex(noteIndex);
    }

    // Normalize selected_text from API to selectedText for consistency
    const normalizedNote = {
      ...note,
      selectedText: note.selected_text || note.selectedText || "",
    };

    setNotePosition({ x, y });
    setEditingNote(normalizedNote);
    setNoteText(note.content || note.text || "");
    setExpandedSelectedText(false);
    setShowNoteInput(true);
  };

  // Cycle Edit Note carousel on this page/section; also seek to that note's CFI.
  const handleNavigateNote = async (direction) => {
    if (!notes || notes.length === 0) return;

    let idx = -1;
    if (editingNote?.id != null) {
      idx = notes.findIndex(
        (n) => n && String(n.id) === String(editingNote.id),
      );
    }
    if (idx < 0) idx = currentNoteIndex;

    const nextIndex = (idx + direction + notes.length) % notes.length;
    const nextNote = notes[nextIndex];
    if (!nextNote || nextIndex === idx) return;

    setCurrentNoteIndex(nextIndex);
    const normalizedNote = {
      ...nextNote,
      selectedText: nextNote.selected_text || nextNote.selectedText || "",
    };
    setEditingNote(normalizedNote);
    setNoteText(nextNote.content || nextNote.text || "");
    setExpandedSelectedText(false);

    const cfiRange = getHighlightCfiRange(nextNote);
    const chapterNumber =
      nextNote.chapter_number != null ? Number(nextNote.chapter_number) : null;
    const fallbackPage =
      nextNote.page_number != null ? Number(nextNote.page_number) : null;

    if (cfiRange) {
      try {
        await navigateToAnnotationCfi({
          cfiRange,
          chapterNumber,
          fallbackPage,
        });
      } catch (err) {
        console.warn("[EPUB] note carousel seek failed:", err);
      }
    } else if (fallbackPage != null && !Number.isNaN(fallbackPage)) {
      if (Number(currentPageRef.current) !== fallbackPage) {
        setCurrentPage(fallbackPage);
      }
    }

    setTimeout(() => {
      const textarea = document.querySelector(".note-input-textarea");
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(
          textarea.value.length,
          textarea.value.length,
        );
      }
    }, 50);
  };

  const handleNextNote = () => {
    handleNavigateNote(1);
  };

  const handlePrevNote = () => {
    handleNavigateNote(-1);
  };

  const handleNoteSave = async () => {
    if (isNoteSaving || !bookId || !noteText.trim()) return;
    setIsNoteSaving(true);

    const pageNum = currentPageRef.current ?? currentPage ?? 1;
    const chapterNum = currentChunkIndexRef.current ?? 1;

    try {
      if (editingNote) {
        if (editingNote.isNew) {
          const cfiRange =
            getHighlightCfiRange(editingNote) ||
            editingNote.cfiRange ||
            getCurrentLocationCfi();
          const payload = {
            page_number: pageNum,
            content: noteText.trim(),
            selected_text: editingNote.selectedText || "",
            chapter_number: editingNote.chapter_number ?? chapterNum,
            ...(cfiRange ? { coordinates: { cfiRange } } : {}),
          };
          // OPTIMISTIC: Call service function but DON'T await it
          // The service function now updates the store immediately and fires API in background
          createNote(payload);
        } else {
          // This is an existing note being edited
          // OPTIMISTIC: Call service function but DON'T await it
          const existingCfi = getHighlightCfiRange(editingNote);
          editNote(editingNote.id, {
            page_number: editingNote.page_number ?? pageNum,
            content: noteText.trim(),
            selected_text:
              editingNote.selected_text ??
              editingNote.selectedText ??
              undefined,
            ...(editingNote.chapter_number != null
              ? { chapter_number: editingNote.chapter_number }
              : {}),
            ...(existingCfi
              ? { coordinates: { cfiRange: existingCfi } }
              : editingNote.coordinates
                ? { coordinates: editingNote.coordinates }
                : {}),
          });
        }
      } else {
        // Blank / click-to-place note — store viewport location CFI when available
        const locationCfi = getCurrentLocationCfi();
        const payload = {
          page_number: pageNum,
          content: noteText.trim(),
          selected_text: "",
          chapter_number: chapterNum,
          ...(locationCfi ? { coordinates: { cfiRange: locationCfi } } : {}),
        };
        // OPTIMISTIC: Call service function but DON'T await it
        createNote(payload);
      }

      // IMMEDIATE: Close editor and clear state (no blocking)
      // refreshNotes() not needed - the store is already updated optimistically
      setShowNoteInput(false);
      setEditingNote(null);
      setNoteText("");
      setExpandedSelectedText(false);

      // Auto-disable notes mode after any save
      setNotesEnabled(false);
    } finally {
      setIsNoteSaving(false);
    }
  };

  const handleNoteCancel = () => {
    setShowNoteInput(false);
    setEditingNote(null);
    setNoteText("");
    setExpandedSelectedText(false);
    setNotesEnabled(false);
  };

  const closeEditingNote = () => {
    setShowNoteInput(false);
    setEditingNote(null);
    setNoteText("");
    setExpandedSelectedText(false);
    setNotesEnabled(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // On touch devices (iPad/mobile), Enter should always create a new line.
      // On desktop, we keep Enter-to-submit as a convenience shortcut.
      const isTouchDevice =
        typeof window !== "undefined" &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0);

      if (isTouchDevice) {
        return;
      }

      e.preventDefault();
      if (isNoteSaving) return;
      handleNoteSave();
    } else if (e.key === "Escape") {
      handleNoteCancel();
    }
  };

  // ---------- CONTEXT MENU ----------
  const handleContextMenuClose = () => {
    setContextMenuVisible(false);
    setSelectionRect(null);
    setSelectedText("");
    contextMenuRangeRef.current = null;
    contextMenuCfiRangeRef.current = null;
    contextMenuContentsRef.current = null;
    setSelectedHighlightOverlay(null);
    setSelectedUnderlineOverlay(null);
  };

  const handleTranslateFromSelection = useCallback(
    async (uiLanguageCode) => {
      const text = (
        selectedText ||
        selectedHighlightOverlay?.content ||
        selectedUnderlineOverlay?.content ||
        ""
      ).trim();
      if (!text) return;

      const position = buildTranslationAnchor(
        selectionRect,
        contextMenuPosition,
      );

      handleContextMenuClose();
      await runTranslation({ text, uiLanguageCode, position });
    },
    [
      contextMenuPosition,
      runTranslation,
      selectedHighlightOverlay?.content,
      selectedText,
      selectedUnderlineOverlay?.content,
      selectionRect,
    ],
  );

  const copyTextWithFallback = useCallback(async (text, contents) => {
    if (!text) return false;

    const targetWindow = contents?.window;
    const targetDocument = contents?.document;

    try {
      if (targetWindow?.navigator?.clipboard?.writeText) {
        await targetWindow.navigator.clipboard.writeText(text);
        return true;
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_e) {
      // Fall back to execCommand path when async clipboard is unavailable/denied.
    }

    const doc = targetDocument || document;
    const body = doc?.body || document.body;
    if (!body) return false;

    let textarea = null;
    try {
      textarea = doc.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.style.pointerEvents = "none";
      body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      let copied = false;
      if (typeof doc.execCommand === "function") {
        copied = doc.execCommand("copy");
      }
      if (!copied && typeof document.execCommand === "function") {
        copied = document.execCommand("copy");
      }

      return copied;
    } catch (_e) {
      return false;
    } finally {
      if (textarea && textarea.parentNode) {
        textarea.parentNode.removeChild(textarea);
      }
    }
  }, []);

  const handleEpubHighlightColor = useCallback(
    async (hex) => {
      setHighlightColor(hex);

      if (selectedHighlightOverlay) {
        const overlay = selectedHighlightOverlay;
        const highlightFromStore = (highlightsFromStore || []).find((h) => {
          if (overlay.highlightId && h.id === overlay.highlightId) return true;
          return getHighlightCfiRange(h) === overlay.cfiRange;
        });
        const highlightId = highlightFromStore?.id || overlay.highlightId;
        if (highlightId) {
          editHighlight(highlightId, { color: hex });
        }
        // Visually refresh: remove + re-add annotation with new color
        try {
          if (renditionRef.current && overlay.cfiRange) {
            try {
              renditionRef.current.annotations.remove(
                overlay.cfiRange,
                "highlight",
              );
            } catch (_e) {
              // ignore
            }
            const colorClass = getEpubHighlightColorClass(hex);
            const highlightStyles = getEpubHighlightStyles(hex);
            const content = overlay.content || "";
            renditionRef.current.annotations.add(
              "highlight",
              overlay.cfiRange,
              { id: highlightId },
              (e) =>
                handleHighlightClick(overlay.cfiRange, highlightId, content, e),
              colorClass,
              highlightStyles,
            );
          }
        } catch (err) {
          console.error("Error recoloring EPUB highlight:", err);
        }
        return;
      }

      try {
        const selectionRangeSnapshot = contextMenuRangeRef.current;
        const selectionCfiRangeSnapshot = contextMenuCfiRangeRef.current;
        const contentsSnapshot = contextMenuContentsRef.current;
        const selectedTextSnapshot = selectedText;
        handleContextMenuClose();
        await applyEpubHighlightSelection({
          selectionRange: selectionRangeSnapshot,
          selectionCfiRange: selectionCfiRangeSnapshot,
          contents: contentsSnapshot,
          selectedTextStr: selectedTextSnapshot,
        });
      } catch (err) {
        console.error("Error applying EPUB highlight:", err);
      }
    },
    [
      getEpubHighlightColorClass,
      getEpubHighlightStyles,
      getHighlightCfiRange,
      handleHighlightClick,
      highlightsFromStore,
      selectedHighlightOverlay,
      selectedText,
      setHighlightColor,
    ],
  );

  const selectionToolbar = useMemo(() => {
    const notesAllowed = isFeatureAllowed(drmPolicy, "notes");
    const highlightsAllowed = isFeatureAllowed(drmPolicy, "highlights");
    const underlineAllowed = isFeatureAllowed(drmPolicy, "underline");
    const copyAllowed = isCopyAllowed(drmPolicy);
    const hasHighlight = Boolean(selectedHighlightOverlay);
    const hasUnderline = Boolean(selectedUnderlineOverlay);
    const canDelete =
      (hasHighlight && highlightsAllowed) || (hasUnderline && underlineAllowed);

    let activeColor = highlightColor || "#FFF59D";
    if (hasHighlight) {
      const overlay = selectedHighlightOverlay;
      const fromStore = (highlightsFromStore || []).find((h) => {
        if (overlay.highlightId && h.id === overlay.highlightId) return true;
        return getHighlightCfiRange(h) === overlay.cfiRange;
      });
      if (fromStore?.color) activeColor = fromStore.color;
    }

    return {
      colors: getHighlightColorOptions(t),
      activeColor,
      canHighlight: highlightsAllowed,
      canUnderline: underlineAllowed,
      canDelete,
      underlineActive: hasUnderline,
      deleteLabel: hasUnderline
        ? t("contextMenu.removeUnderline")
        : t("contextMenu.removeHighlight"),
      onUnderline: underlineAllowed
        ? async () => {
            if (hasUnderline) return;
            try {
              const selectionRangeSnapshot = contextMenuRangeRef.current;
              const selectionCfiRangeSnapshot = contextMenuCfiRangeRef.current;
              const contentsSnapshot = contextMenuContentsRef.current;
              const selectedTextSnapshot = selectedText;
              handleContextMenuClose();
              await applyEpubUnderlineSelection({
                selectionRange: selectionRangeSnapshot,
                selectionCfiRange: selectionCfiRangeSnapshot,
                contents: contentsSnapshot,
                selectedTextStr: selectedTextSnapshot,
              });
            } catch (err) {
              console.error("Error applying EPUB underline:", err);
            }
          }
        : null,
      onHighlightColor: highlightsAllowed ? handleEpubHighlightColor : null,
      onDelete: canDelete
        ? async () => {
            if (hasHighlight) {
              await handleSelectedHighlightDelete();
            } else if (hasUnderline) {
              await handleSelectedUnderlineDelete();
            }
            handleContextMenuClose();
          }
        : null,
      actions: {
        askAi: {
          onClick: () => {
            const text = (
              selectedText ||
              selectedHighlightOverlay?.content ||
              selectedUnderlineOverlay?.content ||
              ""
            ).trim();
            if (!text) return;
            openReadingAssistantAskAi(text);
            handleContextMenuClose();
          },
        },
        copy: {
          onClick: copyAllowed
            ? async () => {
                const contentsSnapshot = contextMenuContentsRef.current;
                const selectedTextSnapshot =
                  selectedText ||
                  selectedHighlightOverlay?.content ||
                  selectedUnderlineOverlay?.content ||
                  "";
                handleContextMenuClose();
                const liveSelectedText =
                  contentsSnapshot?.window
                    ?.getSelection?.()
                    ?.toString?.()
                    ?.trim?.() || "";
                const rawText = (
                  liveSelectedText ||
                  selectedTextSnapshot ||
                  ""
                ).trim();
                if (!rawText) return;
                const limit = getCopyCharLimit(drmPolicy);
                const textToCopy = truncateForCopy(rawText, limit);
                if (limit !== -1 && rawText.length > limit) {
                  showCopyLimitExceededNotification(limit);
                }
                const copied = await copyTextWithFallback(
                  textToCopy,
                  contentsSnapshot,
                );
                if (!copied) {
                  showOperationFailedNotification(t("notifications.operations.copyText"));
                }
              }
            : null,
          disabled: !copyAllowed,
        },
        addNote: {
          onClick: notesAllowed
            ? () => {
                const selectedTextSnapshot =
                  selectedText ||
                  selectedHighlightOverlay?.content ||
                  selectedUnderlineOverlay?.content ||
                  "";
                const cfiSnapshot =
                  contextMenuCfiRangeRef.current ||
                  selectedHighlightOverlay?.cfiRange ||
                  selectedUnderlineOverlay?.cfiRange ||
                  null;
                handleContextMenuClose();
                setNotePosition({ x: 50, y: 50 });
                setEditingNote({
                  id: `temp-${Date.now()}`,
                  content: "",
                  page_number: currentPageRef.current ?? 1,
                  selectedText: selectedTextSnapshot,
                  noteType: "selected",
                  timestamp: new Date().toISOString(),
                  isNew: true,
                  coordinates: cfiSnapshot
                    ? { cfiRange: cfiSnapshot }
                    : undefined,
                  chapter_number: currentChunkIndexRef.current ?? 1,
                });
                setNoteText("");
                setShowNoteInput(true);
                setSelectedText("");
              }
            : null,
          disabled: !notesAllowed,
        },
        translate: {
          onSelectLanguage: handleTranslateFromSelection,
          languageCode: getAiTargetLanguage(),
        },
      },
    };
  }, [
    applyEpubUnderlineSelection,
    copyTextWithFallback,
    drmPolicy,
    getHighlightCfiRange,
    handleEpubHighlightColor,
    handleSelectedHighlightDelete,
    handleSelectedUnderlineDelete,
    handleTranslateFromSelection,
    highlightColor,
    highlightsFromStore,
    openReadingAssistantAskAi,
    selectedHighlightOverlay,
    selectedText,
    selectedUnderlineOverlay,
    t,
  ]);

  if (error) {
    return (
      <div className="epub-viewer">
        <div className="epub-error">
          <h3>{t("epub.errorTitle")}</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            {t("epub.reload")}
          </button>
        </div>
      </div>
    );
  }

  const currentChapterNumber = currentChunkIndex;
  // Determine the CSS class for the wrapper based on view mode
  // In double page mode, we don't use fit-to-width/fit-to-page classes
  const readerViewModeClass = isDoublePageMode
    ? "double-page"
    : readerViewMode === "fit-to-width" || readerViewMode === "fit-to-page"
      ? readerViewMode
      : "";

  const hasPrevChapter = metadata?.chapters?.some(
    (c) => Number(c.chapter_number) < currentChapterNumber,
  );
  const hasNextChapter = metadata?.chapters?.some(
    (c) => Number(c.chapter_number) > currentChapterNumber,
  );

  // Disable prev button only if at chapter start AND no previous chapter exists
  const prevDisabled = isRendering || (atChapterStart && !hasPrevChapter);
  // Disable next button only if at chapter end AND no next chapter exists
  const nextDisabled = isRendering || (atChapterEnd && !hasNextChapter);

  return (
    <div className="epub-viewer active">
      {isRendering && (
        <div className="epub-loading" role="status" aria-live="polite">
          <div className="epub-loading-spinner" aria-hidden="true"></div>
          <p>{t("epub.loading")}</p>
        </div>
      )}

      {/* Navigation buttons - always show for page-by-page navigation.
          In RTL, left advances and right goes back (spatial page-turn UX). */}
      <div className={`epub-chunk-nav-container ${readerViewModeClass}`}>
        <button
          type="button"
          className="epub-chunk-nav epub-chunk-prev"
          onClick={isRtlReading ? handleNextPage : handlePrevPage}
          disabled={isRtlReading ? nextDisabled : prevDisabled}
          aria-label={
            isRtlReading
              ? t("rightToolbar.nextPage")
              : t("rightToolbar.previousPage")
          }
          title={
            isRtlReading
              ? t("rightToolbar.nextPage")
              : t("rightToolbar.previousPage")
          }
        >
          <FaChevronLeft size={32} />
        </button>
        <button
          type="button"
          className="epub-chunk-nav epub-chunk-next"
          onClick={isRtlReading ? handlePrevPage : handleNextPage}
          disabled={isRtlReading ? prevDisabled : nextDisabled}
          aria-label={
            isRtlReading
              ? t("rightToolbar.previousPage")
              : t("rightToolbar.nextPage")
          }
          title={
            isRtlReading
              ? t("rightToolbar.previousPage")
              : t("rightToolbar.nextPage")
          }
        >
          <FaChevronRight size={32} />
        </button>
      </div>

      <div className="epub-scroll-viewport" ref={viewportRef}>
        <div
          key={containerKey}
          ref={containerRef}
          className={`epub-scroll-wrapper ${readerViewModeClass}`}
          style={{
            cursor:
              notesEnabled && !highlightEnabled
                ? "pointer"
                : highlightEnabled
                  ? "text"
                  : "default",
          }}
        >
          {/* Note indicator with carousel navigation */}
          {notes.length > 0 && notes[currentNoteIndex] && (
            <div
              className="note-indicator"
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                zIndex: 1000,
              }}
              onClick={(e) =>
                handleNoteIndicatorClick(notes[currentNoteIndex], e)
              }
              title={t("epub.noteIndicatorTitle", {
                current: currentNoteIndex + 1,
                total: notes.length,
                text: `${
                  notes[currentNoteIndex].content ||
                  notes[currentNoteIndex].text
                    ? (
                        notes[currentNoteIndex].content ||
                        notes[currentNoteIndex].text
                      ).substring(0, 50)
                    : ""
                }${
                  (notes[currentNoteIndex].content ||
                    notes[currentNoteIndex].text) &&
                  (
                    notes[currentNoteIndex].content ||
                    notes[currentNoteIndex].text
                  ).length > 50
                    ? "..."
                    : ""
                }`,
                suffix: notes[currentNoteIndex].selectedText
                  ? t("epub.noteSelectedSuffix")
                  : "",
              })}
            >
              <FaStickyNote size={16} />
              {notes[currentNoteIndex].noteType === "selected" && (
                <span className="note-type-indicator">S</span>
              )}
              {notes.length > 1 && (
                <span className="note-count">
                  {currentNoteIndex + 1}/{notes.length}
                </span>
              )}
            </div>
          )}

          {/* Inline note input - portaled to body so it appears above left sidebar toolbar */}
          {showNoteInput &&
            (!editingNote ||
              editingNote.page_number == null ||
              Number(editingNote.page_number) === Number(currentPage) ||
              Number(editingNote.page_number) === Number(currentSection) ||
              // Keep overlay while cycling notes in the open carousel group
              (notes.length > 0 &&
                editingNote?.id != null &&
                notes.some(
                  (n) => n && String(n.id) === String(editingNote.id),
                ))) &&
            (() => {
              const noteOverlay = (
                <div
                  className="note-input-overlay"
                  style={{
                    position: "fixed",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 10002,
                  }}
                >
                  <div
                    className="note-input-container"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="note-input-header">
                      <span className="note-title">
                        {editingNote
                          ? t("epub.noteTitleEdit")
                          : t("epub.noteTitleAdd")}
                        {notes.length > 1 && notes[currentNoteIndex] && (
                          <span className="note-counter">
                            {t("epub.noteCounter", {
                              current: currentNoteIndex + 1,
                              total: notes.length,
                            })}
                          </span>
                        )}
                      </span>
                      <div
                        className="note-close-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNoteCancel();
                        }}
                      >
                        <FaTimes />
                      </div>
                    </div>

                    {/* Show selected text for selected text notes */}
                    {editingNote?.selectedText && (
                      <div className="note-selected-text">
                        <div className="note-selected-text-label">
                          {t("epub.selectedTextLabel")}
                        </div>
                        <div className="note-selected-text-content">
                          {expandedSelectedText ||
                          editingNote.selectedText.length <= 150 ? (
                            `"${editingNote.selectedText}"`
                          ) : (
                            <>
                              {`"${editingNote.selectedText.substring(0, 150)}..."`}
                              <button
                                className="note-selected-text-expand"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSelectedText(true);
                                }}
                              >
                                {t("common.showMore")}
                              </button>
                            </>
                          )}
                          {expandedSelectedText &&
                            editingNote.selectedText.length > 150 && (
                              <button
                                className="note-selected-text-collapse"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSelectedText(false);
                                }}
                              >
                                {t("common.showLess")}
                              </button>
                            )}
                        </div>
                      </div>
                    )}

                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onClick={(e) => e.stopPropagation()}
                      placeholder={
                        editingNote?.noteType === "selected"
                          ? t("epub.notePlaceholderSelected")
                          : t("epub.notePlaceholder")
                      }
                      autoFocus
                      className="note-input-textarea"
                    />

                    <div className="note-input-footer">
                      <div className="note-footer-left">
                        {notes.length > 1 && (
                          <div className="note-nav-controls">
                            <button
                              type="button"
                              className="note-nav-icon"
                              aria-label={t("rightToolbar.previousPage")}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handlePrevNote();
                              }}
                            >
                              <FaChevronLeft />
                            </button>
                            <button
                              type="button"
                              className="note-nav-icon"
                              aria-label={t("rightToolbar.nextPage")}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleNextNote();
                              }}
                            >
                              <FaChevronRight />
                            </button>
                          </div>
                        )}
                      </div>

                      <button
                        className="note-submit-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isNoteSaving) return;
                          handleNoteSave();
                        }}
                        disabled={!noteText.trim() || isNoteSaving}
                      >
                        {editingNote && !editingNote.isNew
                          ? t("epub.noteActionSave")
                          : t("epub.noteActionCreate")}
                      </button>
                    </div>
                  </div>
                </div>
              );
              return createPortal(noteOverlay, document.body);
            })()}
        </div>
      </div>

      <ContextMenu
        isVisible={contextMenuVisible}
        position={contextMenuPosition}
        selectionRect={selectionRect}
        onClose={handleContextMenuClose}
        selectionToolbar={selectionToolbar}
      />
      <TranslationPopover
        isVisible={translationState.isVisible}
        position={translationState.position}
        sourceText={translationState.sourceText}
        translatedText={translationState.translatedText}
        isLoading={translationState.isLoading}
        error={translationState.error}
        onClose={closeTranslationPopover}
      />
      {selectedHighlightOverlay?.bounds &&
        createPortal(
          <div
            className="epub-annotation-selection-overlay epub-highlight-selection-overlay"
            style={{
              left: `${selectedHighlightOverlay.bounds.left}px`,
              top: `${selectedHighlightOverlay.bounds.top}px`,
              width: `${selectedHighlightOverlay.bounds.width}px`,
              height: `${selectedHighlightOverlay.bounds.height}px`,
            }}
          >
            <div className="epub-annotation-selection-frame" />
          </div>,
          document.body,
        )}
      {selectedUnderlineOverlay?.bounds &&
        createPortal(
          <div
            className="epub-annotation-selection-overlay epub-underline-selection-overlay"
            style={{
              left: `${selectedUnderlineOverlay.bounds.left}px`,
              top: `${selectedUnderlineOverlay.bounds.top}px`,
              width: `${selectedUnderlineOverlay.bounds.width}px`,
              height: `${selectedUnderlineOverlay.bounds.height}px`,
            }}
          >
            <div className="epub-annotation-selection-frame" />
          </div>,
          document.body,
        )}
      {imageLightbox.isOpen &&
        createPortal(
          <div
            className="epub-image-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={t("epub.imageLightboxAria")}
            onClick={closeImageLightbox}
          >
            <div
              className="epub-image-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                className="epub-image-lightbox-image"
                src={imageLightbox.src}
                alt={imageLightbox.alt || t("epub.imageLightboxAria")}
              />
              <button
                type="button"
                className="epub-image-lightbox-close"
                ref={imageLightboxCloseButtonRef}
                tabIndex={0}
                onClick={closeImageLightbox}
                aria-label={t("epub.closeImagePreview")}
                title={t("common.close")}
              >
                <FaTimes size={20} />
              </button>
            </div>
          </div>,
          document.body,
        )}

      <Pullouts enabled={epubReady} />
    </div>
  );
};

export default EpubViewerSimple;
